"use client"

import React, { useState, useRef, useCallback, useEffect } from "react";
import { Box, Typography, Button } from '@mui/material';
import { Description } from '@mui/icons-material';
import { FaMousePointer, FaMinus, FaTrash, FaCode, FaHighlighter, FaPlus } from 'react-icons/fa';
import Editor from '@monaco-editor/react';

// ContentEditable component for rich text editing with highlighting
const ContentEditableDiv = ({ itemId, content, onContentChange, placeholder, highlightColor, onDoubleClick, onTextSelect }) => {
  const divRef = useRef(null);
  const isInternalUpdateRef = useRef(false);
  const lastContentRef = useRef('');
  const selectionTimeoutRef = useRef(null);

  // Convert plain text to HTML if needed (for backward compatibility)
  const getHtmlContent = (text) => {
    if (!text) return '';
    if (text.includes('<')) return text; // Already HTML
    // Convert plain text with line breaks to HTML
    return text.split('\n').map((line, i, arr) => 
      line || (i < arr.length - 1 ? '<br>' : '')
    ).join('').replace(/\n/g, '<br>');
  };

  // Save cursor position
  const saveCursorPosition = () => {
    if (!divRef.current) return null;
    const selection = window.getSelection();
    if (selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(divRef.current);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    return {
      start: preCaretRange.toString().length,
      end: preCaretRange.toString().length
    };
  };

  // Restore cursor position
  const restoreCursorPosition = (position) => {
    if (!divRef.current || !position) return;
    try {
      const selection = window.getSelection();
      const range = document.createRange();
      
      // Simple approach: try to set cursor at the saved position
      // If that fails, place at end
      const textContent = divRef.current.textContent || '';
      const targetPos = Math.min(position.start, textContent.length);
      
      let charCount = 0;
      const walker = document.createTreeWalker(
        divRef.current,
        NodeFilter.SHOW_TEXT,
        null
      );

      let node;
      let targetNode = null;
      let targetOffset = 0;

      while (node = walker.nextNode()) {
        const nodeLength = node.textContent.length;
        const nextCharCount = charCount + nodeLength;
        
        if (charCount <= targetPos && targetPos <= nextCharCount) {
          targetNode = node;
          targetOffset = targetPos - charCount;
          break;
        }
        charCount = nextCharCount;
      }

      if (targetNode) {
        range.setStart(targetNode, targetOffset);
        range.setEnd(targetNode, targetOffset);
        selection.removeAllRanges();
        selection.addRange(range);
      } else {
        // Fallback: place cursor at end
        range.selectNodeContents(divRef.current);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } catch (e) {
      // If restoration fails, just place cursor at end
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(divRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  // Update content when prop changes (but not from user input)
  useEffect(() => {
    // Skip if this is an internal update (user typing)
    if (isInternalUpdateRef.current) {
      return;
    }
    
    if (divRef.current) {
      const htmlContent = getHtmlContent(content);
      const currentHtml = divRef.current.innerHTML;
      
      // Only update if content actually changed from external source
      // And the div is not currently focused (user is not typing)
      const isFocused = document.activeElement === divRef.current;
      
      if (currentHtml !== htmlContent && content !== lastContentRef.current && !isFocused) {
        // Save cursor position before update
        const cursorPos = saveCursorPosition();
        const wasFocused = document.activeElement === divRef.current;
        
        divRef.current.innerHTML = htmlContent;
        lastContentRef.current = content;
        
        // Restore cursor position if it was focused
        if (wasFocused && cursorPos) {
          // Use requestAnimationFrame to ensure DOM is updated
          requestAnimationFrame(() => {
            if (divRef.current) {
              restoreCursorPosition(cursorPos);
              divRef.current.focus();
            }
          });
        }
      }
    }
  }, [content]);

  const handleInput = (e) => {
    isInternalUpdateRef.current = true;
    const htmlContent = e.target.innerHTML;
    lastContentRef.current = htmlContent;
    // Update immediately to prevent cursor jumping
    onContentChange(htmlContent);
    // Keep the flag true for a bit to prevent useEffect from interfering
    setTimeout(() => {
      isInternalUpdateRef.current = false;
    }, 100);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  // Initialize content on mount
  useEffect(() => {
    if (divRef.current && !divRef.current.innerHTML) {
      const htmlContent = getHtmlContent(content);
      divRef.current.innerHTML = htmlContent;
      lastContentRef.current = content;
    }
  }, []); // Only run on mount

  return (
    <>
                    <div
                      ref={divRef}
                      contentEditable
                      data-item-id={itemId}
                      suppressContentEditableWarning
                      onInput={handleInput}
                      onMouseDown={(e) => {
                        // Stop propagation to prevent dragging when clicking on contentEditable
                        e.stopPropagation();
                        // Don't interfere with double-click text selection
                        // Only focus on single click
                        if (e.detail === 1) {
                          if (divRef.current) {
                            divRef.current.focus();
                          }
                        }
                      }}
                      onDoubleClick={(e) => {
                        // Don't stop propagation - allow default double-click text selection
                        // Only stop if we need to prevent dragging, but double-click should allow selection
                        e.stopPropagation();
                        // Let the browser handle text selection naturally
                        // Don't interfere with the default double-click behavior
                        if (onDoubleClick) {
                          onDoubleClick();
                        }
                      }}
                      onClick={(e) => {
                        // Single click: focus the contentEditable for editing
                        e.stopPropagation();
                        if (divRef.current && e.detail === 1) {
                          divRef.current.focus();
                        }
                      }}
                      onMouseUp={(e) => {
                        // Check for text selection
                        if (selectionTimeoutRef.current) {
                          clearTimeout(selectionTimeoutRef.current);
                        }
                        selectionTimeoutRef.current = setTimeout(() => {
                          const selection = window.getSelection();
                          if (selection.rangeCount > 0 && !selection.isCollapsed) {
                            const range = selection.getRangeAt(0);
                            const selectedText = selection.toString();
                            if (selectedText.trim() && divRef.current && divRef.current.contains(range.commonAncestorContainer)) {
                              const rect = range.getBoundingClientRect();
                              const svgRect = divRef.current.closest('svg')?.getBoundingClientRect();
                              if (svgRect && onTextSelect) {
                                onTextSelect({
                                  itemId,
                                  position: {
                                    x: rect.left + rect.width / 2 - svgRect.left,
                                    y: rect.top - svgRect.top - 35 // Position above selection
                                  }
                                });
                              }
                            } else if (onTextSelect) {
                              onTextSelect(null); // Clear selection
                            }
                          } else if (onTextSelect) {
                            onTextSelect(null); // Clear selection
                          }
                        }, 100);
                      }}
                      onPaste={handlePaste}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          borderRadius: '0',
          padding: '6px',
          margin: '0',
          fontSize: '12px',
          fontFamily: 'inherit',
          outline: 'none',
          backgroundColor: 'transparent',
          color: '#374151',
          lineHeight: '1.5',
          boxSizing: 'border-box',
          overflowY: 'auto',
          overflowX: 'hidden',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word'
        }}
        data-placeholder={placeholder}
        onFocus={(e) => {
          if (!e.target.textContent && e.target.getAttribute('data-placeholder')) {
            e.target.textContent = '';
          }
        }}
        onBlur={(e) => {
          if (!e.target.textContent && e.target.getAttribute('data-placeholder')) {
            e.target.textContent = '';
          }
        }}
      />
      <style>{`
        div[contenteditable="true"][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        div[contenteditable="true"] font[style*="background"] {
          background-color: ${highlightColor} !important;
          color: inherit;
          padding: 2px 0;
        }
      `}</style>
    </>
  );
};

export default function DiagramDraw({ 
  problemId, 
  items = [], 
  setItems, 
  connections = [], 
  setConnections,
  itemType = 'class', // 'class', 'entity', or 'api'
  componentType = 'LLD', // 'LLD' or 'HLD'
  onSave,
  saveStatus = 'idle',
  hasUnsavedChanges = false,
  setHasUnsavedChanges,
  documentVisible = false,
  setDocumentVisible = () => {},
  onEvaluate = null, // Optional evaluation callback
  isEvaluating = false, // Evaluation loading state
  hideViewDoc = false, // Hide View Doc button
  onSelectionChange = null, // Callback when selection changes (itemType passed as parameter)
  deselectAllCallback = null, // Callback to receive deselectAll function
  onCopy = null, // Callback when copy is performed (itemType passed as parameter)
  clearCopiedCallback = null, // Callback to receive clearCopiedItems function
  onSaveSnapshot = null // Called with SVG data URL after save
}) {
  const [selectedTool, setSelectedTool] = useState("select");
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [connectionStart, setConnectionStart] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [copiedItems, setCopiedItems] = useState([]);
  // Load highlightColor from localStorage or use default
  const [highlightColor, setHighlightColor] = useState(() => {
    if (problemId) {
      const attemptKey = `${componentType.toLowerCase()}-attempt-${problemId}`;
      const attemptData = localStorage.getItem(attemptKey);
      if (attemptData) {
        try {
          const parsed = JSON.parse(attemptData);
          return parsed.highlightColor || '#ffeb3b';
        } catch (e) {
          return '#ffeb3b';
        }
      }
    }
    return '#ffeb3b'; // Default yellow highlight
  });
  const monacoRef = useRef(null); // Store monaco instance
  const contentEditableRefs = useRef({});
  const editorRefs = useRef({});
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerItemId, setColorPickerItemId] = useState(null);
  const [selectedTextInfo, setSelectedTextInfo] = useState(null); // { itemId, position: { x, y } }
  const autoDeselectTimerRef = useRef(null);
  const hasFormattedOnLoadRef = useRef(false); // Track if we've done initial formatting
  const [copiedConnections, setCopiedConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const hasLoadedDataRef = useRef({ problemId: null, loaded: false }); // Guard to prevent multiple loads per problemId
  
  const svgRef = useRef(null);

  // Capture SVG snapshot — crops to the bounding box of all items, returns a data URL
  const captureSnapshotAsync = (svgEl, itemsList, callback) => {
    if (!svgEl || !itemsList || itemsList.length === 0) { callback(null); return; }
    const PAD = 40;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    itemsList.forEach(s => {
      const x = s.position?.x ?? 0;
      const y = s.position?.y ?? 0;
      const w = s.size?.width ?? 100;
      const h = s.size?.height ?? 100;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x + w > maxX) maxX = x + w;
      if (y + h > maxY) maxY = y + h;
    });
    if (minX === Infinity) { callback(null); return; }
    const vbX = minX - PAD, vbY = minY - PAD;
    const vbW = Math.max((maxX - minX) + PAD * 2, 100);
    const vbH = Math.max((maxY - minY) + PAD * 2, 100);
    try {
      const clone = svgEl.cloneNode(true);
      clone.setAttribute('viewBox', `${vbX} ${vbY} ${vbW} ${vbH}`);
      clone.setAttribute('width', String(vbW));
      clone.setAttribute('height', String(vbH));

      // Remove ALL foreignObject elements — they never render inside <img> SVG data URLs
      clone.querySelectorAll('foreignObject').forEach(fo => fo.remove());

      // Re-add item content as native SVG <text> elements so they render in snapshots
      const svgNS = 'http://www.w3.org/2000/svg';
      const HEADER_H = 30;
      const LINE_H = 14;
      const FONT_SIZE = 11;
      const PAD_X = 6;
      const PAD_Y = 14;

      itemsList.forEach(item => {
        if (!item || !item.position || !item.size) return;
        const { position, size, name } = item;
        const itemGroup = clone.querySelector(`g[data-item-id="${item.id}"]`);
        if (!itemGroup) return;

        // Ensure header name text exists (it may have been a foreignObject input if user was editing)
        const existingHeaderText = itemGroup.querySelector('text');
        if (!existingHeaderText && name) {
          const headerText = document.createElementNS(svgNS, 'text');
          headerText.setAttribute('x', String(position.x + 10));
          headerText.setAttribute('y', String(position.y + 20));
          headerText.setAttribute('font-size', '12');
          headerText.setAttribute('font-weight', '700');
          headerText.setAttribute('fill', 'white');
          headerText.setAttribute('text-anchor', 'start');
          headerText.textContent = name;
          itemGroup.appendChild(headerText);
        }

        // Add content text lines
        const rawContent = item.content || '';
        if (!rawContent.trim()) return;

        // Strip any HTML tags (entity items may store HTML from contentEditable)
        const plainText = rawContent
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<[^>]+>/g, '')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&nbsp;/g, ' ');

        const lines = plainText.split('\n');
        const contentAreaH = size.height - HEADER_H;
        const maxLines = Math.max(1, Math.floor((contentAreaH - PAD_Y) / LINE_H));
        // Approx max chars that fit in width
        const maxChars = Math.max(10, Math.floor((size.width - PAD_X * 2) / 6.5));

        const startX = position.x + PAD_X;
        const startY = position.y + HEADER_H + PAD_Y;

        let lineIdx = 0;
        for (let i = 0; i < lines.length && lineIdx < maxLines; i++) {
          const line = lines[i];
          if (line === '' && i > 0) {
            // Render a blank line as a small gap
            lineIdx++;
            continue;
          }
          const displayLine = line.length > maxChars ? line.slice(0, maxChars - 1) + '\u2026' : line;
          const textEl = document.createElementNS(svgNS, 'text');
          textEl.setAttribute('x', String(startX));
          textEl.setAttribute('y', String(startY + lineIdx * LINE_H));
          textEl.setAttribute('font-size', String(FONT_SIZE));
          textEl.setAttribute('font-family', 'monospace, Consolas, "Courier New"');
          textEl.setAttribute('fill', '#374151');
          textEl.setAttribute('text-anchor', 'start');
          textEl.textContent = displayLine;
          itemGroup.appendChild(textEl);
          lineIdx++;
        }

        // Indicate truncation
        if (lines.length > maxLines) {
          const moreEl = document.createElementNS(svgNS, 'text');
          moreEl.setAttribute('x', String(startX));
          moreEl.setAttribute('y', String(startY + maxLines * LINE_H));
          moreEl.setAttribute('font-size', '10');
          moreEl.setAttribute('font-family', 'monospace');
          moreEl.setAttribute('fill', '#9ca3af');
          moreEl.textContent = '\u2026';
          itemGroup.appendChild(moreEl);
        }
      });

      const svgStr = new XMLSerializer().serializeToString(clone);
      callback('data:image/svg+xml,' + encodeURIComponent(svgStr));
    } catch (e) {
      callback(null);
    }
  };

  // Use refs to store latest values for cleanup
  const itemsRef = useRef(items);
  const connectionsRef = useRef(connections);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  
  const DEFAULT_ITEM_SIZE = { width: 180, height: 80 }; // header (30) + content (50)
  const DEFAULT_API_SIZE = { width: 360, height: 260 }; // Larger size for API components to show all 3 boxes with proper spacing
  const CLASS_PLACEHOLDER = `Describe the class role plus its key attributes and methods.
Example:
Attributes:
- id: UUID
- name: string

Methods:
- getName(): string
- updateName(data)`;
  const ENTITY_PLACEHOLDER = `List entity columns with types, constraints, or notes.
Example:
- id: uuid (PK)
- name: string
- email: string UNIQUE
- created_at: timestamp`;

  // Keep refs in sync with state
  useEffect(() => {
    itemsRef.current = items;
    connectionsRef.current = connections;
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [items, connections, hasUnsavedChanges]);
  const [resizing, setResizing] = useState(null);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  const [initialMousePos, setInitialMousePos] = useState({ x: 0, y: 0 });
  
  // API Block child box state
  const [draggedChildBox, setDraggedChildBox] = useState(null); // { apiItemId, boxType }
  const [childBoxDragOffset, setChildBoxDragOffset] = useState({ x: 0, y: 0 });
  const [editingChildBox, setEditingChildBox] = useState(null); // { apiItemId, boxType }
  const [editingChildBoxText, setEditingChildBoxText] = useState('');

  const itemLabel = itemType === 'class' ? 'Class' : (itemType === 'api' ? 'API' : 'Entity');
  const itemLabelPlural = itemType === 'class' ? 'Classes' : (itemType === 'api' ? 'APIs' : 'Entities');

  // Define updateItems and updateConnections first, before they're used in useEffect
  const updateItems = useCallback((updater) => {
    if (typeof updater === 'function') {
      setItems(prevItems => {
        const newItems = updater(prevItems);
        return newItems;
      });
    } else {
      setItems(updater);
    }
    if (setHasUnsavedChanges) setHasUnsavedChanges(true);
  }, [setItems, setHasUnsavedChanges]);

  const updateConnections = useCallback((updater) => {
    if (!setConnections) return;
    if (typeof updater === 'function') {
      setConnections(prevConnections => {
        const newConnections = updater(prevConnections);
        return newConnections;
      });
    } else {
      setConnections(updater);
    }
    if (setHasUnsavedChanges) setHasUnsavedChanges(true);
  }, [setConnections, setHasUnsavedChanges]);

  // Helper function to deselect all items (internal use)
  const deselectAllItems = useCallback(() => {
    updateItems(prev => prev.map(item => ({ ...item, selected: false })));
    setSelectedConnection(null);
  }, [updateItems]);

  // Function to clear copied items (exposed via callback)
  const clearCopiedItems = useCallback(() => {
    setCopiedItems([]);
    setCopiedConnections([]);
  }, []);

  // Function to deselect all items (exposed via callback)
  const deselectAll = useCallback(() => {
    deselectAllItems();
    // Note: We don't clear copied items here - they are cleared when copying in the other diagram
  }, [deselectAllItems]);

  // Expose deselectAll function via callback
  useEffect(() => {
    if (deselectAllCallback) {
      deselectAllCallback(deselectAll);
    }
  }, [deselectAllCallback, deselectAll]);

  // Expose clearCopiedItems function via callback
  useEffect(() => {
    if (clearCopiedCallback) {
      clearCopiedCallback(clearCopiedItems);
    }
  }, [clearCopiedCallback, clearCopiedItems]);

  // Helper function to select an item and notify selection change
  // Helper function to bring selected items to the front (end of array)
  const bringSelectedToFront = useCallback((itemsArray) => {
    const selected = itemsArray.filter(item => item.selected);
    const unselected = itemsArray.filter(item => !item.selected);
    // Selected items go to the end (rendered on top)
    return [...unselected, ...selected];
  }, []);

  const selectItem = useCallback((itemId) => {
    updateItems(prev => {
      const updated = prev.map(item => ({ ...item, selected: item.id === itemId }));
      // Bring selected item to front
      return bringSelectedToFront(updated);
    });
    setSelectedConnection(null);
    // Notify parent component about selection change
    if (onSelectionChange) {
      onSelectionChange(itemType);
    }
  }, [updateItems, onSelectionChange, itemType, bringSelectedToFront]);

  // Reset initialization flags when problemId changes
  useEffect(() => {
    if (problemId) {
      setHasInitialized(false);
    }
  }, [problemId]);

  // Save to localStorage whenever items or connections change
  useEffect(() => {
    if (!loading && problemId && problemId !== 'undefined' && problemId !== 'null' && hasInitialized) {
      // Save to main attempt localStorage key based on componentType
      const attemptKey = `${componentType.toLowerCase()}-attempt-${problemId}`;
      try {
        const currentAttemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
        
        // Store data based on itemType - flat structure like HLD
        if (itemType === 'class') {
          currentAttemptData.classDiagram = {
            items: items || [],
            connections: connections || [],
            hasUnsavedChanges,
            timestamp: Date.now()
          };
        } else if (itemType === 'entity') {
          // Store entities and relationships directly (not nested in schemaDesign)
          currentAttemptData.schemaEntities = items || [];
          currentAttemptData.schemaRelationships = connections || [];
          currentAttemptData.schemaHasUnsavedChanges = hasUnsavedChanges;
        } else if (itemType === 'api') {
          // Store APIs and relationships directly (not nested in schemaDesign)
          currentAttemptData.apiEntities = items || [];
          currentAttemptData.apiRelationships = connections || [];
          currentAttemptData.apiHasUnsavedChanges = hasUnsavedChanges;
        }
        
        // Save highlightColor preference
        currentAttemptData.highlightColor = highlightColor;
        currentAttemptData.timestamp = Date.now();
        localStorage.setItem(attemptKey, JSON.stringify(currentAttemptData));
      } catch (error) {
        console.error('Failed to save to localStorage:', error);
      }
    }
  }, [items, connections, hasUnsavedChanges, loading, problemId, itemType, hasInitialized, componentType, highlightColor]);

  // Save to localStorage before leaving page
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Silently save to localStorage before leaving
      if (problemId && problemId !== 'undefined' && problemId !== 'null') {
        const attemptKey = `${componentType.toLowerCase()}-attempt-${problemId}`;
        try {
          const currentAttemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
          
          // Store data based on itemType - flat structure like HLD
          if (itemType === 'class') {
            currentAttemptData.classDiagram = {
              items: items || [],
              connections: connections || [],
              hasUnsavedChanges: hasUnsavedChanges || false,
              timestamp: Date.now()
            };
          } else if (itemType === 'entity') {
            // Store entities and relationships directly (not nested in schemaDesign)
            currentAttemptData.schemaEntities = items || [];
            currentAttemptData.schemaRelationships = connections || [];
            currentAttemptData.schemaHasUnsavedChanges = hasUnsavedChanges || false;
          } else if (itemType === 'api') {
            // Store APIs and relationships directly (not nested in schemaDesign)
            currentAttemptData.apiEntities = items || [];
            currentAttemptData.apiRelationships = connections || [];
            currentAttemptData.apiHasUnsavedChanges = hasUnsavedChanges || false;
          }
          
          currentAttemptData.timestamp = Date.now();
          localStorage.setItem(attemptKey, JSON.stringify(currentAttemptData));
        } catch (error) {
          console.error('Failed to save to localStorage on unload:', error);
        }
      }
    };

      window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [items, connections, hasUnsavedChanges, problemId, itemType, componentType]);

  // Save data before component unmounts (when switching steps)
  useEffect(() => {
    return () => {
      // Cleanup: Save data before unmounting using refs to get latest values
      if (problemId && problemId !== 'undefined' && problemId !== 'null') {
        const attemptKey = `${componentType.toLowerCase()}-attempt-${problemId}`;
        try {
          const currentAttemptData = JSON.parse(localStorage.getItem(attemptKey) || '{}');
          
          // Use refs to get the latest values (not stale closure values)
          const latestItems = itemsRef.current || [];
          const latestConnections = connectionsRef.current || [];
          const latestHasUnsavedChanges = hasUnsavedChangesRef.current || false;
          
          // Store data based on itemType - flat structure like HLD
          if (itemType === 'class') {
            currentAttemptData.classDiagram = {
              items: latestItems,
              connections: latestConnections,
              hasUnsavedChanges: latestHasUnsavedChanges,
              timestamp: Date.now()
            };
          } else if (itemType === 'entity') {
            // Store entities and relationships directly (not nested in schemaDesign)
            currentAttemptData.schemaEntities = latestItems;
            currentAttemptData.schemaRelationships = latestConnections;
            currentAttemptData.schemaHasUnsavedChanges = latestHasUnsavedChanges;
          } else if (itemType === 'api') {
            // Store APIs and relationships directly (not nested in schemaDesign)
            currentAttemptData.apiEntities = latestItems;
            currentAttemptData.apiRelationships = latestConnections;
            currentAttemptData.apiHasUnsavedChanges = latestHasUnsavedChanges;
          }
          
          currentAttemptData.timestamp = Date.now();
          localStorage.setItem(attemptKey, JSON.stringify(currentAttemptData));
        } catch (error) {
          console.error('Failed to save to localStorage on unmount:', error);
        }
      }
    };
  }, [problemId, itemType, componentType]);

  // Load data from localStorage - check every time component mounts
  useEffect(() => {
    // Reset formatting flag when problemId changes (page refresh or new problem)
    if (hasLoadedDataRef.current.problemId !== problemId) {
      hasFormattedOnLoadRef.current = false;
    }
    
    if (!problemId) {
      setLoading(false);
      return;
    }
    
    // Guard: Only load data once per problemId
    if (hasLoadedDataRef.current.problemId === problemId && hasLoadedDataRef.current.loaded) {
      return;
    }
    
    // Reset guard when problemId changes
    if (hasLoadedDataRef.current.problemId !== problemId) {
      hasLoadedDataRef.current = { problemId, loaded: false };
    }
    
    const loadData = () => {
      // Always check localStorage first when component mounts
      const attemptKey = `${componentType.toLowerCase()}-attempt-${problemId}`;
      const attemptData = localStorage.getItem(attemptKey);
      
      if (attemptData) {
        try {
          const parsed = JSON.parse(attemptData);
          
          // Load data based on itemType - flat structure like HLD
          let itemsToLoad = null;
          let connectionsToLoad = null;
          
          if (itemType === 'class' && parsed.classDiagram) {
            itemsToLoad = parsed.classDiagram.items;
            connectionsToLoad = parsed.classDiagram.connections;
          } else if (itemType === 'entity') {
            // Load from flat structure (not nested in schemaDesign)
            itemsToLoad = parsed.schemaEntities || [];
            connectionsToLoad = parsed.schemaRelationships || [];
          } else if (itemType === 'api') {
            // Load APIs from flat structure
            itemsToLoad = parsed.apiEntities || [];
            connectionsToLoad = parsed.apiRelationships || [];
          }
          
          if (itemsToLoad !== null && Array.isArray(itemsToLoad)) {
            // Convert old format to new format if needed
            const convertedItems = itemsToLoad.map(item => {
              // Ensure item has proper size with width and height
              const defaultSize = DEFAULT_ITEM_SIZE;
              const itemSize = item.size || defaultSize;
              const size = {
                width: itemSize.width || defaultSize.width,
                height: itemSize.height || defaultSize.height
              };
              
              if (item.content !== undefined) {
                return { 
                  ...item, 
                  content: item.content || '', 
                  size,
                  headerColor: item.headerColor || '#374151', // Preserve headerColor
                  highlights: item.highlights || [] // Preserve highlights
                };
              }
              // Convert old format (attributes/methods or fields) to content string
              const attributes = typeof item.attributes === 'string' 
                ? item.attributes 
                : Array.isArray(item.attributes) 
                  ? item.attributes.join('\n') 
                  : '';
              const methods = typeof item.methods === 'string'
                ? item.methods
                : Array.isArray(item.methods)
                  ? item.methods.join('\n')
                  : '';
              const fields = Array.isArray(item.fields)
                ? item.fields.map(f => typeof f === 'string' ? f : (f.name || f.text || '')).join('\n')
                : '';
              
              let content = attributes;
              if (methods) {
                content += (content ? '\n\nMethods:\n' : 'Methods:\n') + methods;
              }
              if (fields) {
                content = fields;
              }
              
              return { 
                ...item, 
                content: content || '', 
                size,
                headerColor: item.headerColor || '#374151', // Ensure headerColor exists
                highlights: item.highlights || [] // Preserve highlights
              };
            });
            
            // Load highlightColor preference if available
            if (parsed.highlightColor) {
              setHighlightColor(parsed.highlightColor);
            }
            
            updateItems(convertedItems);
            if (setConnections && connectionsToLoad) {
              updateConnections(connectionsToLoad);
            }
            if (itemType === 'class' && parsed.classDiagram) {
              setHasUnsavedChanges(parsed.classDiagram.hasUnsavedChanges || false);
            } else if (itemType === 'entity') {
              setHasUnsavedChanges(parsed.schemaHasUnsavedChanges || false);
            } else if (itemType === 'api') {
              setHasUnsavedChanges(parsed.apiHasUnsavedChanges || false);
            }
            
            // Data loaded from localStorage, no need to call backend
            setLoading(false);
            setHasInitialized(true);
            hasLoadedDataRef.current.loaded = true; // Mark as loaded
            return true; // Indicate data was loaded
          }
        } catch (localError) {
          console.error('Failed to parse attempt data:', localError);
        }
      }
      return false; // No data found in localStorage
    };
    
    // Try to load from localStorage first
    const loadedFromLocal = loadData();
    
    // If no data in localStorage, the parent components (ClassDiagramDraw/SchemaDesigner)
    // will handle backend loading in their own useEffect hooks
    if (!loadedFromLocal) {
      setLoading(false);
      setHasInitialized(true);
      hasLoadedDataRef.current.loaded = true; // Mark as loaded even if no data found
    }
  }, [problemId, itemType, componentType]); // Removed updateItems, updateConnections, setHasUnsavedChanges from deps

  // Function to calculate container bounds based on children and update container size
  const updateContainerBounds = useCallback((itemsArray, containerId, minHeight = null) => {
    const container = itemsArray.find(item => item.id === containerId);
    if (!container || !container.isContainer) return itemsArray;
    
    const children = itemsArray.filter(item => item.parentId === containerId);
    if (children.length === 0) return itemsArray;
    
    // Calculate bounds of all children relative to container position
    let minY = Infinity, maxY = -Infinity;
    children.forEach(child => {
      // Calculate relative positions (only Y for height)
      const relY = child.position.y - container.position.y;
      minY = Math.min(minY, relY);
      maxY = Math.max(maxY, relY + child.size.height);
    });
    
    // Add padding around children (only bottom padding for height)
    const padding = 20;
    const headerHeight = 30;
    minY = Math.max(headerHeight, minY - padding); // Account for header height
    maxY += padding;
    
    // Calculate minimum height needed for 4 component boxes (2x2 grid)
    // Component boxes: 70px height each
    // Padding: 10px top + 10px bottom = 20px
    // Spacing between rows: 10px
    // Content area: 10px (top) + 70px (row 1) + 10px (spacing) + 70px (row 2) + 10px (bottom) = 170px
    // Total minimum: 30px (header) + 170px (content) = 200px
    // Use 240px as minimum to provide extra space
    const componentHeight = 70;
    const componentPadding = 10;
    const componentSpacing = 10;
    const minContentHeight = componentPadding + componentHeight + componentSpacing + componentHeight + componentPadding; // 230px
    const minTotalHeight = headerHeight + minContentHeight + 40; // 300px (260px + 40px extra)
    
    // Always ensure container is large enough to contain all children
    // Use the maximum of calculated height and minimum required height
    const calculatedHeight = maxY;
    let newHeight = Math.max(calculatedHeight, minTotalHeight);
    
    // If minHeight is provided (during manual resize), don't shrink below it
    // This allows manual resizing to expand the container beyond calculated bounds
    if (minHeight !== null && container.size.height > minHeight) {
      newHeight = Math.max(newHeight, container.size.height);
    }
    
    // Only update if height would increase, or if no minHeight constraint (allow shrinking when not manually resizing)
    if (minHeight === null) {
      // Normal update: allow both increase and decrease
      if (newHeight !== container.size.height) {
        return itemsArray.map(item => 
          item.id === containerId 
            ? { ...item, size: { width: container.size.width, height: newHeight } }
            : item
        );
      }
    } else {
      // During manual resize: only expand, never shrink
      if (newHeight > container.size.height) {
        return itemsArray.map(item => 
          item.id === containerId 
            ? { ...item, size: { width: container.size.width, height: newHeight } }
            : item
        );
      }
    }
    
    return itemsArray;
  }, []);

  const deleteSelected = useCallback(() => {
    // Allow deletion of selected items
    const selectedItems = items.filter((item) => item.selected);
    
    if (selectedItems.length > 0) {
      // Collect IDs of items to delete
      const itemsToDelete = new Set(selectedItems.map(item => item.id));
      
      // Track containers that need bounds updates after deletion
      const containersToUpdate = new Set();
      
      // Track parent containers for bounds update
      selectedItems.forEach(item => {
        if (item.parentId) {
          containersToUpdate.add(item.parentId);
        }
      });
      
      // Delete selected items and their children, then update container bounds
      updateItems(prev => {
        let updated = prev.filter(item => !itemsToDelete.has(item.id));
        
        // Update bounds for all affected containers
        containersToUpdate.forEach(containerId => {
          updated = updateContainerBounds(updated, containerId);
        });
        
        return updated;
      });
      
      // Also delete connections that involve deleted items
      if (setConnections) {
        updateConnections(prev => prev.filter(conn => {
          const fromId = itemType === 'class' ? conn.from : conn.fromEntityId;
          const toId = itemType === 'class' ? conn.to : conn.toEntityId;
          return !itemsToDelete.has(fromId) && !itemsToDelete.has(toId);
        }));
      }
      
      // Mark as having unsaved changes
      if (setHasUnsavedChanges) {
        setHasUnsavedChanges(true);
      }
      return;
    }
    
    // Delete selected connection if one is selected
    if (selectedConnection && setConnections) {
      updateConnections(prev => prev.filter((c) => c.id !== selectedConnection));
      setSelectedConnection(null);
      
      // Mark as having unsaved changes
      if (setHasUnsavedChanges) {
        setHasUnsavedChanges(true);
      }
    }
  }, [items, selectedConnection, updateConnections, setConnections, setHasUnsavedChanges, itemType, updateItems, updateContainerBounds]);

  const copySelected = useCallback(() => {
    const selectedItems = items.filter(item => item.selected);
    if (selectedItems.length === 0) {
      return;
    }

    const selectedIds = new Set(selectedItems.map(item => item.id));
    const itemsToCopy = selectedItems.map(item => ({
      ...item,
      selected: false
    }));

    const connectionsToCopy = (connections || [])
      .filter(conn => {
        const fromId = itemType === 'class'
          ? conn.from
          : (conn.fromEntityId || conn.from);
        const toId = itemType === 'class'
          ? conn.to
          : (conn.toEntityId || conn.to);
        return selectedIds.has(fromId) && selectedIds.has(toId);
      })
      .map(conn => ({ ...conn }));

    setCopiedItems(itemsToCopy);
    setCopiedConnections(connectionsToCopy);
    
    // Notify parent component about copy operation
    if (onCopy) {
      onCopy(itemType);
    }
  }, [items, connections, itemType, onCopy]);

  const pasteCopied = useCallback(() => {
    if (copiedItems.length === 0) {
      return;
    }

    const offsetX = 50;
    const offsetY = 50;
    const timestamp = Date.now();
    const idMap = new Map();

    const newItems = copiedItems.map((item, index) => {
      const newId = itemType === 'class'
        ? `class_${timestamp}_${index}_${Math.random().toString(36).slice(2, 7)}`
        : (crypto?.randomUUID ? crypto.randomUUID() : `${timestamp}_${index}_${Math.random().toString(36).slice(2, 7)}`);

      idMap.set(item.id, newId);

      return {
        ...item,
        id: newId,
        position: {
          x: item.position.x + offsetX,
          y: item.position.y + offsetY
        },
        selected: true,
        headerColor: item.headerColor || '#374151' // Preserve color or use default
      };
    });

    const newConnections = copiedConnections.map((conn, index) => {
      const newId = `conn_${timestamp}_${index}_${Math.random().toString(36).slice(2, 6)}`;
      if (itemType === 'class') {
        return {
          ...conn,
          id: newId,
          from: idMap.get(conn.from) || conn.from,
          to: idMap.get(conn.to) || conn.to
        };
      }
      const fromKey = conn.fromEntityId || conn.from;
      const toKey = conn.toEntityId || conn.to;
      return {
        ...conn,
        id: newId,
        fromEntityId: idMap.get(fromKey) || fromKey,
        toEntityId: idMap.get(toKey) || toKey
      };
    });

    updateItems(prev => {
      const deselected = prev.map(item => ({ ...item, selected: false }));
      return [...deselected, ...newItems];
    });

    if (setConnections) {
      updateConnections(prev => [...prev, ...newConnections]);
    }
  }, [copiedItems, copiedConnections, itemType, updateItems, updateConnections, setConnections]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isTextInput = ['INPUT', 'TEXTAREA'].includes(e.target.tagName);

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        if (!isTextInput) {
          e.preventDefault();
          copySelected();
        }
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
        if (!isTextInput) {
          e.preventDefault();
          pasteCopied();
        }
        return;
      }

      if (e.key === "Escape") {
        deselectAllItems();
        setConnectionStart(null);
        setSelectedTool("select");
        setResizing(null);
        setResizeHandle(null);
        setInitialSize({ width: 0, height: 0 });
        setInitialPosition({ x: 0, y: 0 });
        setDraggedItem(null);
        setSelectedConnection(null);
        setEditingItem(null);
        setShowColorPicker(false);
        setColorPickerItemId(null);
      }
      // Keyboard delete functionality removed - use delete button instead
      // if (e.key === "Delete" || e.key === "Backspace") {
      //   deleteSelected();
      // }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [updateItems, deleteSelected, copySelected, pasteCopied]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setResizing(null);
      setResizeHandle(null);
      setInitialSize({ width: 0, height: 0 });
      setInitialPosition({ x: 0, y: 0 });
      setDraggedItem(null);
      setDragOffset({ x: 0, y: 0 });
    };
    document.addEventListener("mouseup", handleGlobalMouseUp);
    return () => document.removeEventListener("mouseup", handleGlobalMouseUp);
  }, []);

  const addItem = () => {
    // Default content for API components
    const defaultApiContent = itemType === 'api'
      ? "params:\nbody:\nheaders:"
      : "";

    // Use larger size for API components
    const defaultSize = itemType === 'api' ? DEFAULT_API_SIZE : DEFAULT_ITEM_SIZE;

    const itemPosition = { x: 100 + items.length * 50, y: 100 + items.length * 50 };

    // For API items, create child boxes automatically
    let children = undefined;
    if (itemType === 'api') {
      children = [
        { type: 'params', text: '' },
        { type: 'body', text: '' },
        { type: 'header', text: '' },
      ];
    }
    
    const newItem = {
      id: itemType === 'class' ? `class_${Date.now()}` : crypto.randomUUID(),
      name: itemType === 'class' ? "NewClass" : (itemType === 'api' ? "NewAPI" : "NewEntity"),
      content: defaultApiContent,
      language: 'java', // Default to Java
      position: itemPosition,
      size: { ...defaultSize },
      selected: false,
      headerColor: '#374151', // Default dark gray color
      parentId: null, // For API diagrams, track parent container
      isContainer: itemType === 'api' ? true : false, // API items are containers
      children: children // Initialize with child boxes for API items
    };
    updateItems(prev => [...prev, newItem]);
    setSelectedTool("select");
  };


  const updateItem = (id, changes) => {
    updateItems(prev => prev.map(item => item.id === id ? { ...item, ...changes } : item));
  };

  // Function to add child boxes to API block
  const addChildBoxesToApiBlock = useCallback((apiItemId) => {
    updateItems(prev => {
      return prev.map(item => {
        if (item.id === apiItemId && itemType === 'api') {
          // Check if child boxes already exist
          if (item.children && item.children.length > 0) {
            return item; // Don't add if already exists
          }
          
          const headerHeight = 30;
          const padding = 10;
          const boxWidth = item.size.width - (padding * 2);
          const boxHeight = 40;
          const boxSpacing = 5;
          const startY = item.position.y + headerHeight + padding;
          
          const childBoxes = [
            { type: 'params', position: { x: item.position.x + padding, y: startY }, size: { width: boxWidth, height: boxHeight }, text: '' },
            { type: 'body', position: { x: item.position.x + padding, y: startY + boxHeight + boxSpacing }, size: { width: boxWidth, height: boxHeight }, text: '' },
            { type: 'header', position: { x: item.position.x + padding, y: startY + (boxHeight + boxSpacing) * 2 }, size: { width: boxWidth, height: boxHeight }, text: '' },
          ];

          // Calculate new height to fit all boxes
          const totalChildHeight = (boxHeight * 3) + (boxSpacing * 2) + (padding * 2);
          const newHeight = headerHeight + totalChildHeight;
          
          return {
            ...item,
            children: childBoxes,
            size: { ...item.size, height: newHeight }
          };
        }
        return item;
      });
    });
    if (setHasUnsavedChanges) setHasUnsavedChanges(true);
  }, [itemType, updateItems, setHasUnsavedChanges]);

  const handleSvgClick = useCallback((e) => {
    if (!svgRef.current) return;
    const target = e.target;
    
    // Close color picker when clicking outside
    if (showColorPicker && !target.closest('[data-color-picker]')) {
      setShowColorPicker(false);
      setColorPickerItemId(null);
    }
    
    // Clear text selection info when clicking outside (but not on the highlight button)
    if (selectedTextInfo && !target.closest('foreignObject') && !target.closest('button')) {
      setSelectedTextInfo(null);
    }
    
    // Don't create items if clicking on existing diagram elements
    // Check if we clicked on a line, text, foreignObject, button, or inside an item group
    // Also check for connection groups
    if (target.tagName === 'line' || 
        target.tagName === 'text' || 
        target.tagName === 'foreignObject' ||
        target.tagName === 'button' ||
        target.closest('g[data-item-id]') ||
        target.closest('g[data-connection-id]') ||
        target.closest('foreignObject')) {
      // If clicking on a line (connection) or connection group, don't clear selection - let the connection's own handler manage it
      if (target.tagName === 'line' || target.closest('g[data-connection-id]')) {
        // Connection click will be handled by its own onClick
        return;
      }
      // Only clear editing item for other elements
      setEditingItem(null);
      return;
    }
    
    // If tool is not "class", just clear selections
    if (selectedTool !== "class") {
      deselectAllItems();
      setEditingItem(null);
      return;
    }
    
    // Get click coordinates relative to SVG
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Clear selections when clicking on empty space
    deselectAllItems();
    setSelectedConnection(null);
    setEditingItem(null);

    // Create new item at click position
    // Ensure position is within visible bounds
    // Use larger size for API components
    const defaultSize = itemType === 'api' ? DEFAULT_API_SIZE : DEFAULT_ITEM_SIZE;
    const itemWidth = defaultSize.width;
    const itemHeight = defaultSize.height;
    const padding = 20;
    
    // Default content for API components
    const defaultApiContent = itemType === 'api'
      ? "params:\nbody:\nheaders:"
      : "";

    // For API items, create child boxes automatically
    let children = undefined;
    if (itemType === 'api') {
      children = [
        { type: 'params', text: '' },
        { type: 'body', text: '' },
        { type: 'header', text: '' },
      ];
    }
    
    const newItem = {
      id: itemType === 'class' ? `class_${Date.now()}` : crypto.randomUUID(),
      name: itemType === 'class' ? "NewClass" : (itemType === 'api' ? "NewAPI" : "NewEntity"),
      content: defaultApiContent,
      language: 'java', // Default to Java
      position: { 
        x: Math.max(padding, Math.min(x - itemWidth / 2, rect.width - itemWidth - padding)), 
        y: Math.max(padding, Math.min(y - itemHeight / 2, rect.height - itemHeight - padding))
      },
      size: { width: itemWidth, height: itemHeight },
      selected: false,
      headerColor: '#374151', // Default dark gray color
      parentId: null, // For API diagrams, track parent container
      isContainer: itemType === 'api' ? true : false, // API items are containers
      children: children // Initialize with child boxes for API items
    };
    
    updateItems(prev => [...prev, newItem]);
    setSelectedTool("select");
  }, [selectedTool, itemType, updateItems, showColorPicker, selectedTextInfo, colorPickerItemId]);

  const handleItemMouseDown = useCallback((e, itemId) => {
    e.stopPropagation();
    if (selectedTool === "line") {
      if (!connectionStart) {
        setConnectionStart(itemId);
        selectItem(itemId);
      } else if (connectionStart !== itemId) {
        const newConnection = {
          id: Date.now().toString(),
          type: "association"
        };
        // Use the appropriate field names based on itemType
        if (itemType === 'class') {
          newConnection.from = connectionStart;
          newConnection.to = itemId;
        } else {
          // For both 'entity' and 'api', use fromEntityId/toEntityId
          newConnection.fromEntityId = connectionStart;
          newConnection.toEntityId = itemId;
        }
        updateConnections(prev => [...prev, newConnection]);
        setConnectionStart(null);
        deselectAllItems();
        // Automatically switch back to select tool after drawing a line
        setSelectedTool("select");
      }
      return;
    }

    if (selectedTool === "select") {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const item = items.find((item) => item.id === itemId);
      if (!item) return;

      setDraggedItem(itemId);
      setDragOffset({
        x: e.clientX - rect.left - item.position.x,
        y: e.clientY - rect.top - item.position.y,
      });
      selectItem(itemId);
    }
  }, [selectedTool, connectionStart, items, itemType, updateItems, updateConnections, selectItem]);

  // Auto-deselect boxes after 15 seconds
  useEffect(() => {
    // Clear any existing timer
    if (autoDeselectTimerRef.current) {
      clearTimeout(autoDeselectTimerRef.current);
      autoDeselectTimerRef.current = null;
    }

    // Check if any item is selected
    const selectedItems = items.filter(item => item.selected);
    if (selectedItems.length > 0) {
      // Set timer to deselect after 15 seconds (middle of 10-20 range)
      autoDeselectTimerRef.current = setTimeout(() => {
        deselectAllItems();
        setColorPickerItemId(null);
        setShowColorPicker(false);
        autoDeselectTimerRef.current = null;
      }, 15000); // 15 seconds
    }

    // Cleanup timer on unmount or when selection changes
    return () => {
      if (autoDeselectTimerRef.current) {
        clearTimeout(autoDeselectTimerRef.current);
        autoDeselectTimerRef.current = null;
      }
    };
  }, [items, updateItems]);

  // Helper function to format Java code manually
  const formatJavaCode = useCallback((code) => {
    if (!code || !code.trim()) return code;
    
    const lines = code.split('\n');
    let indentLevel = 0;
    const indentSize = 4;
    const formatted = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      
      // Skip empty lines but preserve them
      if (!trimmed) {
        formatted.push('');
        continue;
      }
      
      // Decrease indent before closing braces
      if (trimmed === '}' || (trimmed.startsWith('}') && trimmed.length > 1)) {
        indentLevel = Math.max(0, indentLevel - 1);
      }
      
      // Add proper indentation
      const indent = ' '.repeat(indentLevel * indentSize);
      formatted.push(indent + trimmed);
      
      // Increase indent after opening braces (but not if it's a closing brace on same line)
      if (trimmed.endsWith('{') && !trimmed.startsWith('}')) {
        indentLevel++;
      }
    }
    
    return formatted.join('\n');
  }, []);

  // Live snapshot — debounced 600ms, fires on every item/connection change so the
  // document preview always reflects the current canvas without requiring a manual save.
  useEffect(() => {
    if (!hasInitialized || !onSaveSnapshot || items.length === 0) return;
    const timerId = setTimeout(() => {
      requestAnimationFrame(() => {
        if (!svgRef.current) return;
        captureSnapshotAsync(svgRef.current, items, (snapshot) => {
          if (snapshot) onSaveSnapshot(snapshot);
        });
      });
    }, 600);
    return () => clearTimeout(timerId);
  }, [items, connections, hasInitialized]);

  // Restore highlights when items are loaded or updated
  useEffect(() => {
    if (!hasInitialized) return;
    
    items.forEach(item => {
      if (item.highlights && Array.isArray(item.highlights) && item.highlights.length > 0) {
        const editor = editorRefs.current[item.id];
        if (editor) {
          const model = editor.getModel();
          if (model && model.getLineCount() > 0) {
            // Check if decorations already exist
            const existingDecorations = editor._highlightDecorations || [];
            if (existingDecorations.length === 0) {
              // Get monaco from ref or window
              const monaco = monacoRef.current || window.monaco;
              if (monaco) {
                restoreItemHighlights(editor, item, monaco);
              } else {
                // If monaco not available yet, retry after a delay
                setTimeout(() => {
                  const retryEditor = editorRefs.current[item.id];
                  if (retryEditor) {
                    const retryModel = retryEditor.getModel();
                    if (retryModel) {
                      const monacoRetry = monacoRef.current || window.monaco;
                      if (monacoRetry) {
                        restoreItemHighlights(retryEditor, item, monacoRetry);
                      }
                    }
                  }
                }, 300);
              }
            }
          }
        }
      }
    });
    
    // Helper function to restore highlights for an item
    function restoreItemHighlights(editor, item, monacoInstance) {
      const highlightColorToUse = item.highlights[0]?.color || highlightColor;
      
      // Ensure CSS exists
      const styleId = 'monaco-highlight-style';
      let style = document.getElementById(styleId);
      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
      }
      style.textContent = `
        .monaco-editor .highlighted-text {
          background-color: ${highlightColorToUse} !important;
        }
        .monaco-editor .view-line .highlighted-text {
          background-color: ${highlightColorToUse} !important;
        }
        .monaco-editor .view-line .highlighted-text-line {
          background-color: ${highlightColorToUse} !important;
        }
      `;
      
      // Create and apply decorations
      const decorations = item.highlights.map(highlight => {
        const range = new monacoInstance.Range(
          highlight.startLineNumber,
          highlight.startColumn,
          highlight.endLineNumber,
          highlight.endColumn
        );
        return {
          range: range,
          options: {
            inlineClassName: 'highlighted-text',
            className: 'highlighted-text-line',
            isWholeLine: false,
            stickiness: 1
          }
        };
      });
      
      const decorationIds = editor.deltaDecorations([], decorations);
      editor._highlightDecorations = decorationIds;
    }
  }, [items, hasInitialized, highlightColor]);

  // Update editor layouts when items change, and format on first load only
  useEffect(() => {
    // Update all editor layouts when items change
    const updateAllLayoutsAndFormat = () => {
      items.forEach(item => {
        const editor = editorRefs.current[item.id];
        if (editor && typeof editor.layout === 'function') {
          const model = editor.getModel();
          
          // Format on first load only (when page is refreshed)
          if (!hasFormattedOnLoadRef.current && model && item.content && itemType === 'class') {
            try {
              // Try Monaco's format action first
              const formatAction = editor.getAction('editor.action.formatDocument');
              if (formatAction && formatAction.isSupported()) {
                formatAction.run().catch(() => {
                  // Fallback to manual formatting
                  const formatted = formatJavaCode(item.content);
                  if (formatted !== item.content) {
                    model.setValue(formatted);
                  }
                });
              } else {
                // Use manual Java formatting
                const formatted = formatJavaCode(item.content);
                if (formatted !== item.content) {
                  model.setValue(formatted);
                }
              }
            } catch (error) {
              // Fallback to manual formatting
              try {
                const formatted = formatJavaCode(item.content);
                if (formatted !== item.content) {
                  model.setValue(formatted);
                }
              } catch (e) {
                console.warn('Formatting failed:', e);
              }
            }
          }
          
          editor.layout();
        }
      });
      
      // Mark as formatted after first run
      if (!hasFormattedOnLoadRef.current) {
        hasFormattedOnLoadRef.current = true;
      }
    };
    
    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      setTimeout(updateAllLayoutsAndFormat, 200);
    });
  }, [items, formatJavaCode, itemType]);

  const handleMouseMove = useCallback((e) => {
    if (resizing) {
      handleResizeMouseMove(e);
      return;
    }
    
    // Child boxes are handled by CSS, no dragging needed
    
    if (!draggedItem || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - dragOffset.x;
    const y = e.clientY - rect.top - dragOffset.y;
    updateItems(prev => {
      // Find the dragged item to check if it's a container
      const draggedItemData = prev.find(item => item.id === draggedItem);
      if (!draggedItemData) return prev;
      
      // Calculate the delta (change in position)
      const deltaX = x - draggedItemData.position.x;
      const deltaY = y - draggedItemData.position.y;
      
      // Update the dragged item and move child boxes if it's an API block
      let updated = prev.map(item => {
        if (item.id === draggedItem) {
          const newItem = { ...item, position: { x, y } };
          
          // If it's an API block with children, update child box positions
          // Child boxes are handled by CSS, no position updates needed
          
          return newItem;
        }
        
        // If dragging a container, move all child components with it
        if (itemType === 'api' && draggedItemData.isContainer && item.parentId === draggedItem) {
          return {
            ...item,
            position: {
              x: item.position.x + deltaX,
              y: item.position.y + deltaY
            }
          };
        }
        
        return item;
      });
      
      // If dragging a child component, allow free movement and auto-expand container if needed
      if (itemType === 'api' && draggedItemData.parentId) {
        const parentContainer = updated.find(item => item.id === draggedItemData.parentId && item.isContainer);
        if (parentContainer) {
          const padding = 10;
          const headerHeight = 30;
          const minX = parentContainer.position.x + padding;
          const minY = parentContainer.position.y + headerHeight + padding;
          
          // Allow dragging outside bounds - container will expand to fit
          let constrainedX = Math.max(minX, x);
          let constrainedY = Math.max(minY, y);
          
          // Don't constrain to max bounds - let container expand instead
          
          updated = updated.map(item => 
            item.id === draggedItem 
              ? { ...item, position: { x: constrainedX, y: constrainedY } }
              : item
          );
          
          // Update container bounds to contain all children (will expand if needed)
          updated = updateContainerBounds(updated, parentContainer.id);
        }
      }
      
      // Keep selected item at front while dragging
      return bringSelectedToFront(updated);
    });
  }, [draggedItem, dragOffset, resizing, updateItems, bringSelectedToFront, itemType, updateContainerBounds, draggedChildBox, childBoxDragOffset, setHasUnsavedChanges]);

  const handleMouseUp = useCallback(() => {
    setDraggedItem(null);
    setDragOffset({ x: 0, y: 0 });
    setResizing(null);
    setResizeHandle(null);
    setInitialSize({ width: 0, height: 0 });
    setInitialPosition({ x: 0, y: 0 });
  }, [draggedItem]);

  // Calculate required height based on content - no padding
  const calculateContentHeight = useCallback((content, width) => {
    if (!content || !content.trim()) {
      return 50; // Minimum height
    }
    
    // Estimate height based on line count and line height
    // Use accurate line height matching Monaco editor
    const lineHeight = 17; // Monaco editor line height (fontSize 11 + line spacing)
    const lines = content.split('\n');
    let totalLines = 0;
    
    // Account for word wrapping - estimate characters per line accurately
    const charWidth = 6.5; // Character width for fontSize 11
    const availableWidth = Math.max(50, width); // No padding subtracted - full width available
    
    lines.forEach(line => {
      if (line.trim()) {
        const lineLength = line.length;
        // Accurate wrapping calculation
        const wrappedLines = Math.max(1, Math.ceil((lineLength * charWidth) / availableWidth));
        totalLines += wrappedLines;
      } else {
        totalLines += 1; // Empty line still takes space
      }
    });
    
    // Calculate height with NO padding - exact height needed, minimum 50px
    const calculatedHeight = Math.max(50, totalLines * lineHeight);
    return calculatedHeight;
  }, []);

  const handleResizeMouseDown = useCallback((e, itemId, handle) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const item = items.find((item) => item.id === itemId);
    if (!item) return;
    setResizing(itemId);
    setResizeHandle(handle);
    setInitialSize({ ...item.size });
    setInitialPosition({ ...item.position });
    setInitialMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, [items]);

  const handleResizeMouseMove = useCallback((e) => {
    if (!resizing || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const currentMouseX = e.clientX - rect.left;
    const currentMouseY = e.clientY - rect.top;
    const deltaX = currentMouseX - initialMousePos.x;
    const deltaY = currentMouseY - initialMousePos.y;
    updateItems(prev => {
      const item = prev.find((item) => item.id === resizing);
      if (!item) return prev;
      
      const newSize = { ...item.size };
      const newPosition = { ...item.position };
      const headerHeight = 30;
      // No padding - text goes directly to border
      
      switch (resizeHandle) {
        case "w":
          newSize.width = Math.max(120, initialSize.width - deltaX);
          newPosition.x = initialPosition.x + (initialSize.width - newSize.width);
          // Recalculate height based on new width if content exists - no padding
          if (item.content && item.content.trim()) {
            const contentHeight = calculateContentHeight(item.content, newSize.width);
            const currentContentAreaHeight = initialSize.height - headerHeight;
            const minRequiredHeight = headerHeight + contentHeight;
            // Increase height exactly when content exceeds current space
            if (contentHeight > currentContentAreaHeight) {
              newSize.height = minRequiredHeight;
            }
          }
          break;
        case "e":
          newSize.width = Math.max(120, initialSize.width + deltaX);
          newPosition.x = initialPosition.x;
          // Recalculate height based on new width if content exists - no padding
          if (item.content && item.content.trim()) {
            const contentHeight = calculateContentHeight(item.content, newSize.width);
            const currentContentAreaHeight = initialSize.height - headerHeight;
            const minRequiredHeight = headerHeight + contentHeight;
            // Increase height exactly when content exceeds current space
            if (contentHeight > currentContentAreaHeight) {
              newSize.height = minRequiredHeight;
            }
          }
          break;
        case "n":
          // For API containers, use higher minimum (300px), for others use 80px
          const minHeightForResizeN = (itemType === 'api' && item.isContainer) ? 300 : 80;
          newSize.height = Math.max(minHeightForResizeN, initialSize.height - deltaY);
          newPosition.y = initialPosition.y + (initialSize.height - newSize.height);
          break;
        case "s":
          // For API containers, use higher minimum (300px), for others use 80px
          const minHeightForResize = (itemType === 'api' && item.isContainer) ? 300 : 80;
          newSize.height = Math.max(minHeightForResize, initialSize.height + deltaY);
          newPosition.y = initialPosition.y;
          break;
        case "nw":
          newSize.width = Math.max(120, initialSize.width - deltaX);
          const minHeightForResizeNW = (itemType === 'api' && item.isContainer) ? 300 : 80;
          newSize.height = Math.max(minHeightForResizeNW, initialSize.height - deltaY);
          newPosition.x = initialPosition.x + (initialSize.width - newSize.width);
          newPosition.y = initialPosition.y + (initialSize.height - newSize.height);
          // Recalculate height based on new width if content exists - no padding
          if (item.content && item.content.trim()) {
            const contentHeight = calculateContentHeight(item.content, newSize.width);
            const currentContentAreaHeight = newSize.height - headerHeight;
            const minRequiredHeight = headerHeight + contentHeight;
            // Increase height exactly when content exceeds current space
            if (contentHeight > currentContentAreaHeight) {
              newSize.height = minRequiredHeight;
              newPosition.y = initialPosition.y + (initialSize.height - newSize.height);
            }
          }
          break;
        case "ne":
          newSize.width = Math.max(120, initialSize.width + deltaX);
          const minHeightForResizeNE = (itemType === 'api' && item.isContainer) ? 300 : 80;
          newSize.height = Math.max(minHeightForResizeNE, initialSize.height - deltaY);
          newPosition.x = initialPosition.x;
          newPosition.y = initialPosition.y + (initialSize.height - newSize.height);
          // Recalculate height based on new width if content exists - no padding
          if (item.content && item.content.trim()) {
            const contentHeight = calculateContentHeight(item.content, newSize.width);
            const currentContentAreaHeight = newSize.height - headerHeight;
            const minRequiredHeight = headerHeight + contentHeight;
            // Increase height exactly when content exceeds current space
            if (contentHeight > currentContentAreaHeight) {
              newSize.height = minRequiredHeight;
              newPosition.y = initialPosition.y + (initialSize.height - newSize.height);
            }
          }
          break;
        case "sw":
          newSize.width = Math.max(120, initialSize.width - deltaX);
          const minHeightForResizeSW = (itemType === 'api' && item.isContainer) ? 300 : 80;
          newSize.height = Math.max(minHeightForResizeSW, initialSize.height + deltaY);
          newPosition.x = initialPosition.x + (initialSize.width - newSize.width);
          newPosition.y = initialPosition.y;
          // Recalculate height based on new width if content exists - no padding
          if (item.content && item.content.trim()) {
            const contentHeight = calculateContentHeight(item.content, newSize.width);
            const currentContentAreaHeight = newSize.height - headerHeight;
            const minRequiredHeight = headerHeight + contentHeight;
            // Increase height exactly when content exceeds current space
            if (contentHeight > currentContentAreaHeight) {
              newSize.height = minRequiredHeight;
            }
          }
          break;
        case "se":
          newSize.width = Math.max(120, initialSize.width + deltaX);
          const minHeightForResizeSE = (itemType === 'api' && item.isContainer) ? 300 : 80;
          newSize.height = Math.max(minHeightForResizeSE, initialSize.height + deltaY);
          newPosition.x = initialPosition.x;
          newPosition.y = initialPosition.y;
          // Recalculate height based on new width if content exists - no padding
          if (item.content && item.content.trim()) {
            const contentHeight = calculateContentHeight(item.content, newSize.width);
            const currentContentAreaHeight = newSize.height - headerHeight;
            const minRequiredHeight = headerHeight + contentHeight;
            // Increase height exactly when content exceeds current space
            if (contentHeight > currentContentAreaHeight) {
              newSize.height = minRequiredHeight;
            }
          }
          break;
      }
      let updated = prev.map(item => 
        item.id === resizing ? { ...item, size: newSize, position: newPosition } : item
      );
      
      
      return updated;
    });
  }, [resizing, resizeHandle, initialSize, initialPosition, initialMousePos, updateItems, calculateContentHeight]);

  const updateContent = (itemId, value) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    
    // For API items, use scrollHeight from textarea for accurate height (handled in onInput/onChange)
    // For other items, calculate height based on content
    if (itemType === 'api') {
      // Just update content, height will be updated by textarea's onInput handler
      updateItem(itemId, { 
        content: value
        // Height will be updated by textarea's onInput handler for smooth resizing
      });
      
      // If this is a child component, update container bounds
      if (item.parentId) {
        // Use setTimeout to allow height update to complete first
        setTimeout(() => {
          updateItems(prev => {
            return updateContainerBounds(prev, item.parentId);
          });
        }, 100);
      }
      return;
    }
    
    // For non-API items (class/entity), calculate height based on content
    const headerHeight = 30;
    const minContentHeight = 50; // Minimum content height
    const contentHeight = calculateContentHeight(value, item.size.width);
    const newContentHeight = Math.max(minContentHeight, contentHeight);
    const newTotalHeight = headerHeight + newContentHeight;
    
    // Always update height based on calculated content height (both increase and decrease)
    // This ensures the box expands when content is added and shrinks when deleted
    updateItem(itemId, { 
      content: value,
      size: {
        ...item.size,
        height: newTotalHeight
      }
    });
    
    // Update editor layout immediately to reflect new height (no scrollbars) - only for Monaco editor (class/entity)
    const editor = editorRefs.current[itemId];
    if (editor && typeof editor.layout === 'function') {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        editor.layout();
      });
    }
  };

  const updateItemColor = useCallback((itemId, color) => {
    if (!itemId || !color) {
      return;
    }
    updateItem(itemId, { headerColor: color });
    setShowColorPicker(false);
    setColorPickerItemId(null);
  }, [updateItem]);

  // Format code in a specific editor - with Java-specific formatting
  const formatEditorCode = useCallback((itemId) => {
    const editor = editorRefs.current[itemId];
    if (!editor) {
      console.warn('Editor not found for item:', itemId);
      return;
    }

    try {
      const model = editor.getModel();
      if (!model) {
        console.warn('Model not found for editor:', itemId);
        return;
      }

      // Focus the editor first
      editor.focus();
      
      // Get the item to check language
      const item = items.find(i => i.id === itemId);
      const languageId = item?.language || 'java';
      const content = model.getValue();
      
      // Try Monaco's format action first
      editor.trigger('format', 'editor.action.formatDocument', {});
      
      // Also try getting the action, with manual formatting as fallback
      setTimeout(async () => {
        try {
          const formatAction = editor.getAction('editor.action.formatDocument');
          if (formatAction && formatAction.isSupported()) {
            await formatAction.run();
          } else {
            // Fallback: Manual Java formatting
            if (languageId === 'java' && content && content.trim()) {
              const formatted = formatJavaCode(content);
              if (formatted !== content) {
                model.setValue(formatted);
              }
            }
          }
        } catch (err) {
          // Fallback: Manual Java formatting
          if (languageId === 'java' && content && content.trim()) {
            const formatted = formatJavaCode(content);
            if (formatted !== content) {
              model.setValue(formatted);
            }
          }
        }
        // Clear selection and update layout
        const lineCount = model.getLineCount();
        const lastLineLength = model.getLineLength(lineCount);
        editor.setPosition({ lineNumber: lineCount, column: lastLineLength + 1 });
        editor.layout();
      }, 200);
    } catch (error) {
      console.error('Error formatting code:', error);
    }
  }, [items, formatJavaCode]);

  // Handle highlighting selected text
  const handleHighlight = useCallback(() => {
    if (selectedTextInfo && selectedTextInfo.itemId) {
      // Handle Monaco Editor highlighting for API/entity items
      const editor = editorRefs.current[selectedTextInfo.itemId];
      if (editor) {
        const selection = editor.getSelection();
        if (selection && !selection.isEmpty()) {
          const model = editor.getModel();
          if (model) {
            const decorations = editor.deltaDecorations([], [
              {
                range: selection,
                options: {
                  inlineClassName: 'highlighted-text',
                  className: 'highlighted-text-line'
                }
              }
            ]);
            
            // Store decoration IDs for later removal
            if (!editor._highlightDecorations) {
              editor._highlightDecorations = [];
            }
            editor._highlightDecorations.push(...decorations);
            
            // Apply background color using inline style
            const startLine = selection.startLineNumber;
            const endLine = selection.endLineNumber;
            const startCol = selection.startColumn;
            const endCol = selection.endColumn;
            
            // Convert hex to rgba for Monaco
            const hex = highlightColor.replace('#', '');
            const r = parseInt(hex.substr(0, 2), 16);
            const g = parseInt(hex.substr(2, 2), 16);
            const b = parseInt(hex.substr(4, 2), 16);
            const rgbaColor = `rgba(${r}, ${g}, ${b}, 0.3)`;
            
            // Add CSS for highlighting if not already added
            const styleId = 'monaco-highlight-style';
            if (!document.getElementById(styleId)) {
              const style = document.createElement('style');
              style.id = styleId;
              style.textContent = `
                .monaco-editor .highlighted-text {
                  background-color: ${highlightColor} !important;
                }
                .monaco-editor .view-line .highlighted-text {
                  background-color: ${highlightColor} !important;
                }
              `;
              document.head.appendChild(style);
            }
            
            // Create decoration with background color using inline style
            const newDecorations = editor.deltaDecorations([], [
              {
                range: selection,
                options: {
                  inlineClassName: 'highlighted-text',
                  className: 'highlighted-text-line',
                  isWholeLine: false,
                  stickiness: 1,
                  hoverMessage: { value: 'Highlighted text' }
                }
              }
            ]);
            
            // Store decoration IDs for this editor
            if (!editor._highlightDecorations) {
              editor._highlightDecorations = [];
            }
            editor._highlightDecorations.push(...newDecorations);
            
            // Store highlight range in item metadata for persistence
            const highlightRange = {
              startLineNumber: selection.startLineNumber,
              startColumn: selection.startColumn,
              endLineNumber: selection.endLineNumber,
              endColumn: selection.endColumn,
              color: highlightColor
            };
            
            // Get existing highlights or create new array
            const currentItem = items.find(i => i.id === selectedTextInfo.itemId);
            if (currentItem) {
              const existingHighlights = currentItem.highlights || [];
              // Check if this range already exists (avoid duplicates)
              const rangeExists = existingHighlights.some(h => 
                h.startLineNumber === highlightRange.startLineNumber &&
                h.startColumn === highlightRange.startColumn &&
                h.endLineNumber === highlightRange.endLineNumber &&
                h.endColumn === highlightRange.endColumn
              );
              
              if (!rangeExists) {
                updateItem(selectedTextInfo.itemId, {
                  highlights: [...existingHighlights, highlightRange]
                });
              }
            }
            
            // Clear selection
            editor.setSelection(selection);
            setSelectedTextInfo(null);
            return;
          }
        }
      }
    }
    
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0);
      const selectedText = selection.toString();
      
      if (selectedText.trim()) {
        try {
          const activeEditor = document.activeElement;
          if (activeEditor && activeEditor.contentEditable === 'true') {
            // Check if the selected text is already highlighted
            const container = range.commonAncestorContainer;
            const parentElement = container.nodeType === Node.TEXT_NODE 
              ? container.parentElement 
              : container;
            
            // Check if the selection is within a highlighted element
            let isHighlighted = false;
            let highlightElement = null;
            
            // Walk up the DOM tree to find if we're inside a highlighted element
            let element = parentElement;
            while (element && element !== activeEditor) {
              if (element.nodeType === Node.ELEMENT_NODE) {
                const bgColor = window.getComputedStyle(element).backgroundColor;
                // Check if it has a background color that's not transparent
                if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent' && bgColor !== 'rgb(0, 0, 0)') {
                  // Check if it matches our highlight color (approximately)
                  const rgb = bgColor.match(/\d+/g);
                  if (rgb && rgb.length >= 3) {
                    const r = parseInt(rgb[0]);
                    const g = parseInt(rgb[1]);
                    const b = parseInt(rgb[2]);
                    // Convert highlightColor hex to RGB for comparison
                    const hex = highlightColor.replace('#', '');
                    if (hex.length === 6) {
                      const highlightR = parseInt(hex.substr(0, 2), 16);
                      const highlightG = parseInt(hex.substr(2, 2), 16);
                      const highlightB = parseInt(hex.substr(4, 2), 16);
                      
                      // Allow some tolerance for color matching
                      if (Math.abs(r - highlightR) < 20 && 
                          Math.abs(g - highlightG) < 20 && 
                          Math.abs(b - highlightB) < 20) {
                        isHighlighted = true;
                        highlightElement = element;
                        break;
                      }
                    }
                  }
                }
              }
              element = element.parentElement;
            }
            
            if (isHighlighted && highlightElement) {
              // Remove highlight by removing background color
              // Select the highlighted element
              const newRange = document.createRange();
              newRange.selectNodeContents(highlightElement);
              selection.removeAllRanges();
              selection.addRange(newRange);
              
              // Remove the background color
              document.execCommand('backColor', false, 'transparent');
              
              // Clean up the HTML - remove empty spans or spans with transparent background
              let updatedHtml = activeEditor.innerHTML;
              // Remove spans with transparent or no background
              updatedHtml = updatedHtml.replace(/<span[^>]*style="[^"]*background-color:\s*transparent[^"]*"[^>]*>(.*?)<\/span>/gi, '$1');
              updatedHtml = updatedHtml.replace(/<span[^>]*style="[^"]*"[^>]*>(.*?)<\/span>/gi, '$1'); // Remove empty style spans
              
              const itemId = activeEditor.getAttribute('data-item-id');
              if (itemId) {
                updateContent(itemId, updatedHtml);
              }
            } else {
              // Apply highlight
              document.execCommand('backColor', false, highlightColor);
              const itemId = activeEditor.getAttribute('data-item-id');
              if (itemId) {
                updateContent(itemId, activeEditor.innerHTML);
              }
            }
          } else {
            // If no active editor, try to find one
            const contentEditables = document.querySelectorAll('[contenteditable="true"]');
            if (contentEditables.length > 0) {
              const editor = contentEditables[0];
              editor.focus();
              // Try again after focusing
              setTimeout(() => {
                const newSelection = window.getSelection();
                if (newSelection.rangeCount > 0 && !newSelection.isCollapsed) {
                  document.execCommand('backColor', false, highlightColor);
                  const itemId = editor.getAttribute('data-item-id');
                  if (itemId) {
                    updateContent(itemId, editor.innerHTML);
                  }
                }
              }, 10);
            }
          }
        } catch (error) {
          console.error('Error highlighting text:', error);
        }
      }
    }
  }, [highlightColor, updateContent, selectedTextInfo]);

  const updateItemName = (itemId, name) => {
    updateItem(itemId, { name });
  };

  const getItemCenter = (item) => {
    return {
      x: item.position.x + item.size.width / 2,
      y: item.position.y + item.size.height / 2,
    };
  };

  const getItemBorderPoint = (item, fromPoint) => {
    const center = getItemCenter(item);
    const dx = fromPoint.x - center.x;
    const dy = fromPoint.y - center.y;
    if (dx === 0 && dy === 0) return center;
    const halfWidth = item.size.width / 2;
    const halfHeight = item.size.height / 2;
    const slope = dy / dx;
    let borderX, borderY;
    if (Math.abs(dx) / halfWidth > Math.abs(dy) / halfHeight) {
      borderX = center.x + (dx > 0 ? halfWidth : -halfWidth);
      borderY = center.y + slope * (borderX - center.x);
    } else {
      borderY = center.y + (dy > 0 ? halfHeight : -halfHeight);
      borderX = center.x + (borderY - center.y) / slope;
    }
    return { x: borderX, y: borderY };
  };

  const renderItem = (item) => {
    if (!item || !item.id) {
      return null;
    }
    
    const { position, size, selected } = item;
    const headerHeight = 30;
    // No padding - text goes directly to border
    const horizontalPadding = 0;
    const verticalPadding = 0;
    
    // Calculate content area height - no padding, exact fit
    const contentAreaHeight = size.height - headerHeight;
    
    // Calculate foreignObject position and dimensions
    // For API items, position slightly lower to ensure first line is visible
    const foreignObjectX = position.x + horizontalPadding;
    const foreignObjectY = itemType === 'api' 
      ? position.y + headerHeight + 1  // Add 1px offset to ensure first line is visible
      : position.y + headerHeight + verticalPadding;
    const foreignObjectWidth = size.width;
    const foreignObjectHeight = contentAreaHeight;

    return (
      <g key={item.id} data-item-id={item.id}>
        {/* Main box background - draggable on single click */}
        <rect
          x={position.x}
          y={position.y}
          width={size.width}
          height={size.height}
          fill={itemType === 'api' ? '#f9fafb' : 'white'}
          stroke={selected ? "#2c3e50" : (itemType === 'api' ? '#d1d5db' : '#e1e8ed')}
          strokeWidth={selected ? 2 : 1}
          rx={itemType === 'api' ? '6' : '0'}
          ry={itemType === 'api' ? '6' : '0'}
          className="cursor-move"
          onMouseDown={(e) => {
            // Single click: select and allow dragging
            if (e.detail === 1) {
              handleItemMouseDown(e, item.id);
            }
          }}
          onClick={(e) => {
            // Single click: select the box
            if (e.detail === 1) {
              e.stopPropagation();
              selectItem(item.id);
              // Close color picker if clicking on a different box
              if (colorPickerItemId && colorPickerItemId !== item.id) {
                setShowColorPicker(false);
                setColorPickerItemId(null);
              }
            }
          }}
        />
        
        {/* Header - draggable on single click, editable on double click */}
        <rect
          x={position.x}
          y={position.y}
          width={size.width}
          height={headerHeight + (itemType === 'api' ? 6 : 0)}
          fill={item.headerColor || '#374151'}
          stroke={selected ? (item.headerColor || "#374151") : (itemType === 'api' ? '#d1d5db' : '#e1e8ed')}
          strokeWidth={selected ? 2 : 1}
          rx={itemType === 'api' ? '6' : '0'}
          ry={itemType === 'api' ? '6' : '0'}
          className="cursor-move"
          onMouseDown={(e) => {
            // Single click: select and allow dragging
            if (e.detail === 1) {
            e.stopPropagation();
              handleItemMouseDown(e, item.id);
            }
          }}
          onClick={(e) => {
            // Single click: select the box
            if (e.detail === 1) {
            e.stopPropagation();
            selectItem(item.id);
              // Close color picker if clicking on a different box
              if (colorPickerItemId && colorPickerItemId !== item.id) {
                setShowColorPicker(false);
                setColorPickerItemId(null);
              }
            }
          }}
          onDoubleClick={(e) => {
            // Double click: edit header
            e.stopPropagation();
            setEditingItem(item.id);
          }}
        />
        
        {editingItem === item.id ? (
          <foreignObject x={position.x + 10} y={position.y + 6} width={size.width - 20} height={20}>
            <input
              type="text"
              value={item.name}
              onChange={(e) => updateItemName(item.id, e.target.value)}
              onFocus={(e) => {
                // Select all text when input is focused
                e.target.select();
              }}
              onBlur={() => setEditingItem(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                  setEditingItem(null);
                }
                // Allow Ctrl+A/Cmd+A to select all
                if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
                  e.preventDefault();
                  e.target.select();
                }
                // Allow Delete and Backspace to work normally
                // Don't prevent default for these keys
              }}
              onClick={(e) => {
                // Allow normal text selection on click
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                // Allow normal text selection on mouse down
                e.stopPropagation();
              }}
              placeholder={itemType === 'api' ? 'GET /api/v1/resource' : ''}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                outline: 'none',
                fontSize: '12px',
                fontWeight: '700',
                textAlign: 'left',
                background: 'transparent',
                color: 'white',
                padding: '0',
                fontFamily: itemType === 'api' ? 'monospace' : 'inherit',
                lineHeight: '20px',
                userSelect: 'text',
                WebkitUserSelect: 'text',
                MozUserSelect: 'text',
                msUserSelect: 'text'
              }}
              autoFocus
            />
          </foreignObject>
        ) : (
          <>
          {(() => {
            const sharedTextProps = {
              className: "cursor-pointer",
              onDoubleClick: (e) => { e.stopPropagation(); setEditingItem(item.id); },
              onClick: (e) => { e.stopPropagation(); selectItem(item.id); },
              onMouseDown: (e) => { if (e.detail === 1) { e.stopPropagation(); handleItemMouseDown(e, item.id); } }
            };
            if (itemType === 'api') {
              const methodMatch = item.name.match(/^(GET|POST|PUT|DELETE|PATCH)\s+(.+)$/i);
              if (methodMatch) {
                const method = methodMatch[1].toUpperCase();
                const path = methodMatch[2];
                const badgeColor = { GET: '#22c55e', POST: '#3b82f6', PUT: '#f59e0b', DELETE: '#ef4444', PATCH: '#a855f7' }[method] || '#6b7280';
                const badgeW = method.length * 6.5 + 10;
                return (
                  <>
                    <rect x={position.x + 8} y={position.y + 7} width={badgeW} height={16} rx="3" ry="3" fill={badgeColor} {...sharedTextProps} />
                    <text x={position.x + 8 + badgeW / 2} y={position.y + 19} textAnchor="middle" fontSize="9" fontWeight="700" fill="white" style={{ pointerEvents: 'none' }}>
                      {method}
                    </text>
                    <text x={position.x + 8 + badgeW + 5} y={position.y + 20} textAnchor="start" fontSize="11" fontWeight="500" fill="white" fontFamily="monospace" {...sharedTextProps}>
                      {path}
                    </text>
                  </>
                );
              }
            }
            return (
              <text
                x={position.x + 10}
                y={position.y + 20}
                textAnchor="start"
                fontSize="12"
                fontWeight="700"
                fill="white"
                {...sharedTextProps}
              >
                {item.name}
              </text>
            );
          })()}
            {/* Format Code Button - Only visible when selected and item has code editor (class type) */}
            {selected && itemType === 'class' && (
              <foreignObject 
                x={position.x + size.width - 72} 
                y={position.y + 1} 
                width="32" 
                height="28"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  formatEditorCode(item.id);
                }}
              >
                <button
                  title="Format code"
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    transition: 'all 0.2s',
                    outline: 'none',
                    fontWeight: 'normal'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = 'rgba(255, 255, 255, 1)';
                    e.target.style.transform = 'scale(1.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                    e.target.style.transform = 'scale(1)';
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <FaCode style={{ fontSize: '12px' }} />
                </button>
              </foreignObject>
            )}
            {/* Highlight Button - Only visible when text is selected in this item */}
            {selectedTextInfo && selectedTextInfo.itemId === item.id && (
              <foreignObject 
                x={position.x + size.width - (itemType === 'class' ? 108 : 72)} 
                y={position.y + 1} 
                width="32" 
                height="28"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleHighlight();
                  setSelectedTextInfo(null);
                }}
              >
                <button
                  title="Highlight selected text"
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    fontSize: '12px',
                    color: '#ffeb3b',
                    transition: 'all 0.2s',
                    outline: 'none',
                    fontWeight: 'normal'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#fdd835';
                    e.target.style.transform = 'scale(1.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#ffeb3b';
                    e.target.style.transform = 'scale(1)';
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <FaHighlighter style={{ fontSize: '12px' }} />
                </button>
              </foreignObject>
            )}
            {/* Color Picker Button - Only visible when selected */}
            {selected && (
              <foreignObject 
                x={position.x + size.width - 36} 
                y={position.y + 1} 
                width="32" 
                height="28"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (colorPickerItemId === item.id && showColorPicker) {
                    setShowColorPicker(false);
                    setColorPickerItemId(null);
                  } else {
                    setColorPickerItemId(item.id);
                    setShowColorPicker(true);
                  }
                }}
              >
                <button
                  title="Change box color"
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    transition: 'all 0.2s',
                    outline: 'none',
                    fontWeight: 'normal'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = 'rgba(255, 255, 255, 1)';
                    e.target.style.transform = 'scale(1.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                    e.target.style.transform = 'scale(1)';
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  🎨
                </button>
              </foreignObject>
            )}
          </>
        )}

        {/* For API items, add a transparent overlay rect for dragging when textarea is not focused */}
        {itemType === 'api' && (
          <rect
            x={foreignObjectX}
            y={foreignObjectY}
            width={foreignObjectWidth}
            height={foreignObjectHeight}
            fill="transparent"
            style={{ pointerEvents: 'auto', cursor: 'move' }}
            onMouseDown={(e) => {
              // Only handle drag if textarea is not focused
              const textarea = e.target.parentElement?.querySelector('textarea');
              if (textarea && document.activeElement !== textarea) {
                handleItemMouseDown(e, item.id);
              }
            }}
            onClick={(e) => {
              // Only handle click if textarea is not focused
              const textarea = e.target.parentElement?.querySelector('textarea');
              if (textarea && document.activeElement !== textarea) {
                selectItem(item.id);
              }
            }}
          />
        )}
        <foreignObject 
          x={foreignObjectX} 
          y={foreignObjectY} 
          width={foreignObjectWidth} 
          height={foreignObjectHeight}
          style={{ 
            pointerEvents: 'auto', 
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}
        >
          {itemType === 'api' ? (
            // Show textarea for API containers only if no child boxes exist
            item.isContainer && (!item.children || item.children.length === 0) ? (
              // Simple textarea for API containers with smooth auto-resize
              <textarea
              key={item.id} // Key ensures re-render when item changes
              value={item.content || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                updateContent(item.id, newValue);
              }}
              onInput={(e) => {
                // Smooth auto-resize on input (handles typing, paste, etc.)
                const textarea = e.target;
                textarea.style.height = 'auto';
                const scrollHeight = textarea.scrollHeight;
                const minHeight = 50;
                const newHeight = Math.max(minHeight, scrollHeight);
                textarea.style.height = newHeight + 'px';
                
                // Update box height to match textarea (grows from bottom, top stays fixed)
                const headerHeight = 30;
                const newTotalHeight = headerHeight + newHeight;
                updateItem(item.id, {
                  size: {
                    ...item.size,
                    height: newTotalHeight
                  }
                  // Position stays the same - box grows from bottom
                });
              }}
              onMouseDown={(e) => {
                // If textarea is not focused, initiate drag manually
                if (document.activeElement !== e.target) {
                  // Prevent text selection
                  e.preventDefault();
                  // Manually trigger drag by calling handleItemMouseDown logic
                  const rect = svgRef.current?.getBoundingClientRect();
                  if (rect && item.id && selectedTool === "select") {
                    const itemData = items.find((it) => it.id === item.id);
                    if (itemData) {
                      setDraggedItem(item.id);
                      setDragOffset({
                        x: e.clientX - rect.left - itemData.position.x,
                        y: e.clientY - rect.top - itemData.position.y,
                      });
                      selectItem(item.id);
                    }
                  }
                  return;
                }
                // If focused, stop propagation to allow text editing
                e.stopPropagation();
              }}
              onDoubleClick={(e) => {
                // Double click to focus and edit text
                e.stopPropagation();
                e.target.focus();
              }}
              onClick={(e) => {
                // Single click - if not focused, allow dragging (don't stop propagation)
                // If focused, allow text selection
                if (document.activeElement !== e.target) {
                  return; // Don't stop propagation, allow drag
                }
                e.stopPropagation(); // Stop only when focused for text editing
              }}
              onFocus={(e) => {
                // Focus textarea for editing
                e.stopPropagation();
                // Initialize height on focus for smooth resizing
                const textarea = e.target;
                textarea.style.height = 'auto';
                const scrollHeight = textarea.scrollHeight;
                const minHeight = 50;
                const newHeight = Math.max(minHeight, scrollHeight);
                textarea.style.height = newHeight + 'px';
              }}
              ref={(textarea) => {
                // Initialize height when textarea is mounted or content changes
                if (textarea) {
                  textarea.style.height = 'auto';
                  const scrollHeight = textarea.scrollHeight;
                  const minHeight = 50;
                  const newHeight = Math.max(minHeight, scrollHeight);
                  textarea.style.height = newHeight + 'px';
                  
                  // Update box height if it doesn't match
                  const headerHeight = 30;
                  const expectedTotalHeight = headerHeight + newHeight;
                  if (Math.abs(item.size.height - expectedTotalHeight) > 1) {
                    updateItem(item.id, {
                      size: {
                        ...item.size,
                        height: expectedTotalHeight
                      }
                    });
                  }
                }
              }}
              style={{
                width: '100%',
                height: 'auto',
                minHeight: '50px',
                border: 'none',
                outline: 'none',
                padding: '6px',
                fontSize: '12px',
                fontFamily: 'inherit',
                resize: 'none',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                color: '#374151',
                lineHeight: '1.5',
                boxSizing: 'border-box',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                margin: 0,
                display: 'block',
                pointerEvents: 'auto',
                cursor: 'move',
                transition: 'none' // No transition for instant resize
              }}
              placeholder="Enter text here..."
              onBlur={(e) => {
                // When blurring, ensure cursor goes back to move
                e.target.style.cursor = 'move';
              }}
            />
            ) : null
          ) : (
            // Monaco editor for class/entity items
            <div style={{ 
              width: '100%', 
              height: '100%',
              overflow: 'hidden',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              padding: 0,
              margin: 0
            }}>
              <Editor
                key={`editor-${item.id}`}
                height="100%"
                width="100%"
                language={item.language || 'java'}
                theme="vs"
                value={item.content || ''}
                onChange={(value) => {
                  updateContent(item.id, value || '');
                }}
              options={{
                fontSize: 11,
                minimap: { enabled: false },
                scrollBeyondLastLine: false, // No scrolling - box height increases instead
                scrollBeyondLastColumn: 0, // No horizontal scrolling
                wordWrap: 'on',
                automaticLayout: true,
                lineNumbers: 'off',
                folding: false,
                lineDecorationsWidth: 0,
                lineNumbersMinChars: 0,
                glyphMargin: false,
                renderLineHighlight: 'none',
                overviewRulerLanes: 0,
                hideCursorInOverviewRuler: true,
                overviewRulerBorder: false,
                // Formatting options
                formatOnPaste: true,
                formatOnType: false, // Don't format on every keystroke, only on paste/mount
                // No scrolling - disable all scroll capabilities
                scrollbar: {
                  vertical: 'hidden',
                  horizontal: 'hidden',
                  verticalScrollbarSize: 0,
                  horizontalScrollbarSize: 0,
                  useShadows: false,
                  verticalHasArrows: false,
                  horizontalHasArrows: false
                },
                wrappingIndent: 'indent',
                wrappingStrategy: 'advanced',
                // Ensure editor respects container bounds
                fixedOverflowWidgets: true,
                // Disable scrolling completely
                mouseWheelScrollSensitivity: 0,
                fastScrollSensitivity: 0,
                // Disable mouse wheel scrolling
                disableLayerHinting: true
              }}
              onMount={async (editor, monaco) => {
                // Store editor reference and monaco instance
                editorRefs.current[item.id] = editor;
                if (monaco && !monacoRef.current) {
                  monacoRef.current = monaco;
                }
                
                // Prevent editor from capturing drag events
                editor.onMouseDown((e) => {
                  if (e.target.type === 1) { // Mouse target
                    e.stopPropagation();
                  }
                });
                
                // Ensure editor respects container bounds - no scrolling
                editor.updateOptions({
                  scrollBeyondLastLine: false,
                  scrollBeyondLastColumn: 0
                });
                
                // Ensure CSS style exists for highlighting
                const styleId = 'monaco-highlight-style';
                let style = document.getElementById(styleId);
                if (!style) {
                  style = document.createElement('style');
                  style.id = styleId;
                  document.head.appendChild(style);
                }
                
                // Restore highlight decorations if they exist
                if (item.highlights && Array.isArray(item.highlights) && item.highlights.length > 0) {
                  // Use the highlight color from the first highlight or the current highlightColor
                  const highlightColorToUse = item.highlights[0]?.color || highlightColor;
                  
                  // Update CSS with the correct highlight color
                  style.textContent = `
                    .monaco-editor .highlighted-text {
                      background-color: ${highlightColorToUse} !important;
                    }
                    .monaco-editor .view-line .highlighted-text {
                      background-color: ${highlightColorToUse} !important;
                    }
                    .monaco-editor .view-line .highlighted-text-line {
                      background-color: ${highlightColorToUse} !important;
                    }
                  `;
                  
                  // Wait for editor to be fully ready before applying decorations
                  const restoreHighlights = () => {
                    const model = editor.getModel();
                    if (model && model.getLineCount() > 0) {
                      // Create decorations for all highlights
                      const decorations = item.highlights.map(highlight => {
                        const range = new monaco.Range(
                          highlight.startLineNumber,
                          highlight.startColumn,
                          highlight.endLineNumber,
                          highlight.endColumn
                        );
                        return {
                          range: range,
                          options: {
                            inlineClassName: 'highlighted-text',
                            className: 'highlighted-text-line',
                            isWholeLine: false,
                            stickiness: 1,
                            hoverMessage: { value: 'Highlighted text' }
                          }
                        };
                      });
                      
                      // Apply decorations
                      const decorationIds = editor.deltaDecorations([], decorations);
                      editor._highlightDecorations = decorationIds;
                    } else {
                      // Retry if model is not ready yet
                      setTimeout(restoreHighlights, 50);
                    }
                  };
                  
                  // Start restoration after a short delay to ensure editor is initialized
                  setTimeout(restoreHighlights, 200);
                } else {
                  // Ensure CSS style exists even if no highlights
                  style.textContent = `
                    .monaco-editor .highlighted-text {
                      background-color: ${highlightColor} !important;
                    }
                    .monaco-editor .view-line .highlighted-text {
                      background-color: ${highlightColor} !important;
                    }
                    .monaco-editor .view-line .highlighted-text-line {
                      background-color: ${highlightColor} !important;
                    }
                  `;
                }
                
                // Track text selection for entity items to show highlight button (not for API - using textarea)
                if (itemType === 'entity') {
                  const currentItemId = item.id;
                  editor.onDidChangeCursorSelection((e) => {
                    const selection = editor.getSelection();
                    if (selection && !selection.isEmpty()) {
                      const currentItem = items.find(i => i.id === currentItemId);
                      if (currentItem) {
                        const headerHeight = 30;
                        const startPos = selection.getStartPosition();
                        const lineNumber = startPos.lineNumber;
                        const lineHeight = 17; // Monaco line height
                        const selectionY = currentItem.position.y + headerHeight + ((lineNumber - 1) * lineHeight);
                        setSelectedTextInfo({
                          itemId: currentItemId,
                          position: {
                            x: currentItem.position.x + (currentItem.size.width / 2),
                            y: selectionY - 15
                          }
                        });
                      }
                    } else {
                      // Only clear if this editor's selection was cleared
                      if (selectedTextInfo && selectedTextInfo.itemId === currentItemId) {
                        setSelectedTextInfo(null);
                      }
                    }
                  });
                  
                  // Clear selection info when clicking outside
                  editor.onMouseDown((e) => {
                    setTimeout(() => {
                      const selection = editor.getSelection();
                      if (!selection || selection.isEmpty()) {
                        if (selectedTextInfo && selectedTextInfo.itemId === currentItemId) {
                          setSelectedTextInfo(null);
                        }
                      }
                    }, 100);
                  });
                }
                
                // Format on first load only (when page is refreshed), then update layout
                const formatAndLayout = async () => {
                  if (editor && editor.getModel()) {
                    const model = editor.getModel();
                    const languageId = item.language || 'java';
                    const content = model.getValue();
                    
                    // Format on first load only
                    if (!hasFormattedOnLoadRef.current && content && content.trim() && itemType === 'class') {
                      try {
                        // Try Monaco's format action first
                        editor.trigger('format', 'editor.action.formatDocument', {});
                        
                        setTimeout(async () => {
                          try {
                            const formatAction = editor.getAction('editor.action.formatDocument');
                            if (formatAction && formatAction.isSupported()) {
                              await formatAction.run();
                            } else {
                              // Fallback: Use manual Java formatting
                              if (languageId === 'java' && content && content.trim()) {
                                const formatted = formatJavaCode(content);
                                if (formatted !== content) {
                                  model.setValue(formatted);
                                }
                              }
                            }
                          } catch (err) {
                            // Fallback: Use manual Java formatting
                            if (languageId === 'java' && content && content.trim()) {
                              const formatted = formatJavaCode(content);
                              if (formatted !== content) {
                                model.setValue(formatted);
                              }
                            }
                          }
                          editor.layout();
                        }, 200);
                      } catch (error) {
                        // Fallback: Use manual Java formatting
                        if (languageId === 'java' && content && content.trim()) {
                          try {
                            const formatted = formatJavaCode(content);
                            if (formatted !== content) {
                              model.setValue(formatted);
                            }
                          } catch (e) {
                            console.warn('Manual formatting failed:', e);
                          }
                        }
                        editor.layout();
                      }
                    } else {
                      // Just update layout if already formatted
                      editor.layout();
                    }
                  }
                };
                
                // Format and update layout when editor is mounted/re-rendered
                setTimeout(formatAndLayout, 200);
              }}
              beforeUnmount={(editor) => {
                // Clean up editor reference
                delete editorRefs.current[item.id];
              }}
              />
            </div>
          )}
        </foreignObject>
        
        {/* Render API Block Content */}
        {itemType === 'api' && item.children && item.children.length > 0 && (
          <foreignObject
            x={position.x}
            y={position.y + headerHeight}
            width={size.width}
            height={Math.max(size.height - headerHeight, 160)}
            style={{ pointerEvents: 'auto', overflow: 'visible' }}
          >
            <div className="api-block-content" data-api-content={item.id}>
              {item.children.map((childBox, index) => {
                const boxType = childBox.type;
                const boxLabel = boxType === 'header' ? 'Headers' : boxType.charAt(0).toUpperCase() + boxType.slice(1);
                const accentColor =
                  boxType === 'params' ? '#0284c7' :
                  boxType === 'body'   ? '#059669' :
                  boxType === 'header' ? '#d97706' : '#6b7280';
                const bgColor =
                  boxType === 'params' ? 'rgba(186,230,253,0.25)' :
                  boxType === 'body'   ? 'rgba(167,243,208,0.25)' :
                  boxType === 'header' ? 'rgba(253,230,138,0.25)' : 'rgba(255,255,255,0.9)';

                return (
                  <div
                    key={`${item.id}-${boxType}-${index}`}
                    className="api-child-box"
                    style={{ borderLeftColor: accentColor, background: bgColor }}
                  >
                    <label className="api-child-label" style={{ color: accentColor }}>
                      {boxLabel}
                    </label>
                    <textarea
                      className="api-child-input"
                      value={childBox.text || ''}
                      onChange={(e) => {
                        const newText = e.target.value;

                        // Auto-resize textarea
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';

                        // Update text
                        updateItems(prev => prev.map(apiItem => {
                          if (apiItem.id === item.id && apiItem.children) {
                            const updatedChildren = apiItem.children.map(cb =>
                              cb.type === boxType ? { ...cb, text: newText } : cb
                            );

                            // Calculate new API block height based on all child boxes
                            const contentDiv = e.target.closest('.api-block-content');
                            if (contentDiv) {
                              // Use requestAnimationFrame to ensure DOM has updated
                              requestAnimationFrame(() => {
                                const totalHeight = Array.from(contentDiv.children).reduce((sum, child) => {
                                  return sum + child.offsetHeight;
                                }, 0);
                                const headerHeight = 30;
                                const padding = 10; // CSS padding value
                                const gap = 6; // CSS gap value
                                const gaps = (contentDiv.children.length - 1) * gap;
                                const newApiHeight = headerHeight + totalHeight + (padding * 2) + gaps;

                                updateItems(prevItems => prevItems.map(ai =>
                                  ai.id === item.id ? {
                                    ...ai,
                                    size: {
                                      ...ai.size,
                                      height: Math.max(ai.size.height, newApiHeight)
                                    }
                                  } : ai
                                ));
                              });
                            }

                            return {
                              ...apiItem,
                              children: updatedChildren
                            };
                          }
                          return apiItem;
                        }));
                        if (setHasUnsavedChanges) setHasUnsavedChanges(true);
                      }}
                      onFocus={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                      ref={(textarea) => {
                        if (textarea) {
                          textarea.style.height = 'auto';
                          textarea.style.height = textarea.scrollHeight + 'px';
                        }
                      }}
                      placeholder={`e.g. ${boxType === 'params' ? 'page=1&limit=10' : boxType === 'body' ? '{ "name": "...", "email": "..." }' : 'Authorization: Bearer ...'}`}
                    />
                  </div>
                );
              })}
            </div>
          </foreignObject>
        )}
      </g>
    );
  };

  const renderResizeHandles = (item) => {
    if (!item.selected) return null;
    const { position, size } = item;
    const handleSize = 8;
    const handles = [
      // Corner handles
      { id: "nw", x: position.x - handleSize / 2, y: position.y - handleSize / 2, cursor: "nw-resize" },
      { id: "ne", x: position.x + size.width - handleSize / 2, y: position.y - handleSize / 2, cursor: "ne-resize" },
      { id: "sw", x: position.x - handleSize / 2, y: position.y + size.height - handleSize / 2, cursor: "sw-resize" },
      { id: "se", x: position.x + size.width - handleSize / 2, y: position.y + size.height - handleSize / 2, cursor: "se-resize" },
      // Edge handles
      { id: "n", x: position.x + size.width / 2 - handleSize / 2, y: position.y - handleSize / 2, cursor: "n-resize" },
      { id: "s", x: position.x + size.width / 2 - handleSize / 2, y: position.y + size.height - handleSize / 2, cursor: "s-resize" },
      { id: "w", x: position.x - handleSize / 2, y: position.y + size.height / 2 - handleSize / 2, cursor: "w-resize" },
      { id: "e", x: position.x + size.width - handleSize / 2, y: position.y + size.height / 2 - handleSize / 2, cursor: "e-resize" },
    ];
    return (
      <g>
        {handles.map((handle) => (
          <rect
            key={`${item.id}-${handle.id}`}
            x={handle.x}
            y={handle.y}
            width={handleSize}
            height={handleSize}
            fill="#3b82f6"
            stroke="#ffffff"
            strokeWidth={2}
            rx="2"
            ry="2"
            style={{ cursor: handle.cursor }}
            onMouseDown={(e) => handleResizeMouseDown(e, item.id, handle.id)}
          />
        ))}
      </g>
    );
  };

  const handleConnectionClick = useCallback((e, connectionId) => {
    e.stopPropagation();
    e.preventDefault();
    // Always allow connection selection when select tool is active
    if (selectedTool === "select") {
      // First deselect all items (but don't clear connection selection yet)
      updateItems(prev => prev.map(item => ({ ...item, selected: false })));
      // Then set the selected connection
      setSelectedConnection(connectionId);
    }
  }, [selectedTool, updateItems]);

  const renderConnection = (connection) => {
    const fromId = itemType === 'class' ? connection.from : connection.fromEntityId;
    const toId = itemType === 'class' ? connection.to : connection.toEntityId;
    const fromItem = items.find((item) => item.id === fromId);
    const toItem = items.find((item) => item.id === toId);
    if (!fromItem || !toItem) return null;

    const fromCenter = getItemCenter(fromItem);
    const toCenter = getItemCenter(toItem);
    const fromBorder = getItemBorderPoint(fromItem, toCenter);
    const toBorder = getItemBorderPoint(toItem, fromCenter);
    const isSelected = selectedConnection === connection.id;

    return (
      <g 
        key={connection.id}
        data-connection-id={connection.id}
      >
        {/* Invisible thick line for easier clicking - this catches the clicks */}
        <line
          x1={fromBorder.x}
          y1={fromBorder.y}
          x2={toBorder.x}
          y2={toBorder.y}
          stroke="transparent"
          strokeWidth={24}
          className="cursor-pointer"
          onMouseDown={(e) => handleConnectionClick(e, connection.id)}
          onClick={(e) => handleConnectionClick(e, connection.id)}
          style={{ pointerEvents: 'all', cursor: 'pointer' }}
        />
        {/* Visible line */}
        <line
          x1={fromBorder.x}
          y1={fromBorder.y}
          x2={toBorder.x}
          y2={toBorder.y}
          stroke={isSelected ? "#3b82f6" : "#000000"}
          strokeWidth={1}
          markerEnd={isSelected ? "url(#arrowhead-selected)" : "url(#arrowhead)"}
          className="cursor-pointer"
          onMouseDown={(e) => handleConnectionClick(e, connection.id)}
          onClick={(e) => handleConnectionClick(e, connection.id)}
          style={{ pointerEvents: 'all', cursor: 'pointer' }}
        />
      </g>
    );
  };


  if (loading) {
    return (
      <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <Box 
        sx={{ 
          p: 1.13, 
          backgroundColor: '#f8f9fa',
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
          {itemLabelPlural}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <button
            onClick={() => {
              setSelectedTool("select");
              setConnectionStart(null);
            }}
            style={{
              backgroundColor: selectedTool === "select" ? '#007bff' : 'white',
              color: selectedTool === "select" ? 'white' : 'black',
              border: selectedTool === "select" ? '2px solid #007bff' : '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FaMousePointer style={{ fontSize: '12px' }} />
          </button>
          {/* Show Main Box button for entities/classes, API button for APIs */}
          {itemType !== 'api' ? (
            <button
              onClick={() => {
                setSelectedTool("class");
                setConnectionStart(null);
              }}
              style={{
                marginLeft: 4,
                backgroundColor: selectedTool === "class" ? '#007bff' : 'white',
                color: selectedTool === "class" ? 'white' : 'black',
                border: selectedTool === "class" ? '2px solid #007bff' : '1px solid #ccc',
                padding: '4px 8px',
                borderRadius: 4,
                height: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {itemLabel}
            </button>
          ) : (
            <button
              onClick={() => {
                setSelectedTool("class");
                setConnectionStart(null);
              }}
              style={{
                marginLeft: 4,
                backgroundColor: selectedTool === "class" ? '#007bff' : 'white',
                color: selectedTool === "class" ? 'white' : 'black',
                border: selectedTool === "class" ? '2px solid #007bff' : '1px solid #ccc',
                padding: '4px 8px',
                borderRadius: 4,
                height: '32px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px'
              }}
              title="Add API"
            >
              <FaCode style={{ fontSize: '12px' }} />
              <span style={{ fontSize: '12px' }}>API</span>
            </button>
          )}
          <button
            onClick={() => setSelectedTool("line")}
            style={{
              marginLeft: 4,
              backgroundColor: selectedTool === "line" ? '#007bff' : 'white',
              color: selectedTool === "line" ? 'white' : 'black',
              border: selectedTool === "line" ? '2px solid #007bff' : '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FaMinus style={{ fontSize: '12px' }} />
          </button>
          <div style={{ borderLeft: '1px solid #ccc', height: '20px', margin: '0 8px' }} />
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Ensure delete works for both items and connections
              if (selectedConnection || items.some((item) => item.selected)) {
                deleteSelected();
              }
            }}
            disabled={!items.some((item) => item.selected) && !selectedConnection}
            style={{
              marginLeft: 4,
              backgroundColor: 'white',
              color: '#dc3545',
              border: '1px solid #ccc',
              padding: '4px 8px',
              borderRadius: 4,
              height: '32px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: (items.some((item) => item.selected) || selectedConnection) ? 1 : 0.5,
              cursor: (items.some((item) => item.selected) || selectedConnection) ? 'pointer' : 'not-allowed',
              pointerEvents: (items.some((item) => item.selected) || selectedConnection) ? 'auto' : 'none'
            }}
            title={items.some((item) => item.selected) ? "Delete selected " + itemLabelPlural.toLowerCase() : selectedConnection ? "Delete selected connection" : "Select an item or connection to delete"}
          >
            <FaTrash style={{ fontSize: '12px' }} />
          </button>
          <div style={{ borderLeft: '1px solid #ccc', height: '20px', margin: '0 8px' }} />
          {onSave && (
            <Button
              variant="contained"
              onClick={() => {
                onSave();
                if (onSaveSnapshot && svgRef.current && items.length > 0) {
                  captureSnapshotAsync(svgRef.current, items, (snapshot) => {
                    if (snapshot) onSaveSnapshot(snapshot);
                  });
                }
              }}
              size="small"
              disabled={saveStatus === 'saving'}
              sx={{
                backgroundColor: 
                  saveStatus === 'success' ? '#008080' :
                  saveStatus === 'error' ? '#f44336' :
                  saveStatus === 'saving' ? '#9e9e9e' :
                  hasUnsavedChanges ? '#ff9800' : '#008080',
                '&:hover': {
                  backgroundColor: 
                    saveStatus === 'success' ? '#006666' :
                    saveStatus === 'error' ? '#d32f2f' :
                    saveStatus === 'saving' ? '#9e9e9e' :
                    hasUnsavedChanges ? '#fb8c00' : '#006666'
                },
                textTransform: 'none',
                fontWeight: 600,
                transition: 'background-color 0.3s ease'
              }}
            >
              {saveStatus === 'saving' ? 'Saving...' : 
               saveStatus === 'success' ? 'Saved!' : 
               saveStatus === 'error' ? 'Error' : 'Save'}
            </Button>
          )}
          {onEvaluate && itemType !== 'api' && (
            <>
              <Button
                variant="contained"
                onClick={onEvaluate}
                disabled={isEvaluating || items.length === 0}
                size="small"
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: '#666'
                  },
                  textTransform: 'none',
                  fontWeight: 600,
                  marginLeft: 1,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)'
                }}
              >
                {isEvaluating ? 'Evaluating...' : 'Evaluate'}
              </Button>
              <div style={{ borderLeft: '1px solid #ccc', height: '20px', margin: '0 8px' }} />
            </>
          )}
          {!hideViewDoc && (
            <Button
              variant="outlined"
              onClick={() => setDocumentVisible(!documentVisible)}
              startIcon={<Description />}
              size="small"
              sx={{
                borderColor: '#008080',
                color: '#008080',
                '&:hover': {
                  backgroundColor: '#e3f2fd',
                  borderColor: '#006666'
                },
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {documentVisible ? 'Hide' : 'View Doc'}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ 
        flex: 1,
        margin: '5px',
        border: '2px solid #ddd',
        borderRadius: '8px',
        backgroundColor: '#fafafa',
        overflow: 'auto',
        position: 'relative',
        minHeight: '600px'
      }}>
        <svg
          ref={svgRef}
          style={{
            width: '100%',
            height: '2000px',
            cursor: selectedTool === "select" ? 'default' : 'crosshair',
            backgroundColor: '#ffffff',
            display: 'block'
          }}
          onClick={handleSvgClick}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <defs>
            <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#e0e0e0" strokeWidth="0.5" />
            </pattern>
            <marker
              id="arrowhead"
              markerWidth="6"
              markerHeight="5"
              refX="6"
              refY="2.5"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <polygon points="0 0, 6 2.5, 0 5" fill="#000000" />
            </marker>
            <marker
              id="arrowhead-selected"
              markerWidth="6"
              markerHeight="5"
              refX="6"
              refY="2.5"
              orient="auto"
              markerUnits="userSpaceOnUse"
            >
              <polygon points="0 0, 6 2.5, 0 5" fill="#3b82f6" />
            </marker>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
          {/* Render containers first, then components on top */}
          {items && Array.isArray(items) && items.length > 0 && items
            .filter(item => !item.parentId || !item.isContainer) // Containers and non-child items
            .map(renderItem)}
          {items && Array.isArray(items) && items.length > 0 && items
            .filter(item => item.parentId && !item.isContainer) // Child components
            .map(renderItem)}
          {items && Array.isArray(items) && items.length > 0 && items.map(renderResizeHandles)}
          {connections && connections.map(renderConnection)}
          
          {/* Color Picker Popup - Positioned near the selected box */}
          {showColorPicker && colorPickerItemId && (() => {
            const selectedItem = items.find(item => item.id === colorPickerItemId);
            if (!selectedItem) return null;
            
            // Calculate position relative to the SVG - position to the right of the box
            const popupX = selectedItem.position.x + selectedItem.size.width + 10;
            const popupY = Math.max(10, selectedItem.position.y);
            
            return (
              <foreignObject
                data-color-picker
                x={popupX}
                y={popupY}
                width="220"
                height="280"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <div
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    padding: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    width: '100%',
                    boxSizing: 'border-box',
                    pointerEvents: 'auto',
                    overflow: 'hidden'
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, fontSize: '14px', width: '100%' }}>
                    Select Box Color
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '8px', 
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    {[
                      { name: 'Dark Gray', color: '#374151' },
                      { name: 'Blue', color: '#3b82f6' },
                      { name: 'Green', color: '#10b981' },
                      { name: 'Purple', color: '#8b5cf6' },
                      { name: 'Red', color: '#ef4444' },
                      { name: 'Orange', color: '#f59e0b' },
                      { name: 'Teal', color: '#14b8a6' },
                      { name: 'Pink', color: '#ec4899' },
                      { name: 'Indigo', color: '#6366f1' },
                      { name: 'Yellow', color: '#eab308' },
                      { name: 'Cyan', color: '#06b6d4' },
                      { name: 'Custom Gray', color: '#363a40' }
                    ].map((colorOption) => (
                      <div
                        key={colorOption.color}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (colorPickerItemId) {
                            updateItemColor(colorPickerItemId, colorOption.color);
                          }
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          backgroundColor: colorOption.color,
                          borderRadius: '4px',
                          cursor: 'pointer',
                          border: '2px solid transparent',
                          transition: 'all 0.2s',
                          pointerEvents: 'auto'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.border = '2px solid #007bff';
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.border = '2px solid transparent';
                          e.target.style.transform = 'scale(1)';
                        }}
                        title={colorOption.name}
                      />
                    ))}
                  </Box>
                </div>
              </foreignObject>
            );
          })()}
          
        </svg>
        
        <style>{`
          .api-block-content {
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 8px;
            box-sizing: border-box;
            gap: 6px;
            overflow: visible;
          }
          .api-child-box {
            display: flex;
            flex-direction: column;
            width: 100%;
            min-height: 42px;
            border: 1px solid #e5e7eb;
            border-left-width: 3px;
            border-radius: 5px;
            padding: 6px 8px;
            box-sizing: border-box;
            flex-shrink: 0;
            transition: box-shadow 0.15s ease;
          }
          .api-child-box:focus-within {
            box-shadow: 0 0 0 2px rgba(0,0,0,0.06);
          }
          .api-child-label {
            font-size: 9px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.6px;
            margin-bottom: 4px;
          }
          .api-child-input {
            width: 100%;
            min-height: 18px;
            border: none;
            outline: none;
            background: transparent;
            color: #374151;
            font-size: 11px;
            padding: 0;
            margin: 0;
            font-family: 'SFMono-Regular', Consolas, 'Courier New', monospace;
            resize: none;
            overflow: hidden;
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            line-height: 1.5;
            box-sizing: border-box;
            flex-grow: 1;
          }
          .api-child-input:focus {
            outline: none;
          }
          .api-child-input::placeholder {
            color: #9ca3af;
            font-style: italic;
          }
        `}</style>

        {items.length === 0 && (
          <Box sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none' // Allow clicks to pass through to SVG
          }}>
            <Box sx={{ textAlign: 'center', pointerEvents: 'none' }}>
              <Typography variant="h6" sx={{ 
                color: '#666',
                mb: 2,
                fontWeight: 400,
                pointerEvents: 'none'
              }}>
                No {itemLabelPlural.toLowerCase()} yet. Click "{itemLabel}" tool and click on canvas to add one.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

